// Chat panel fullscreen para Interfaz V2.
//
// Cuando el user manda su primer mensaje en el prompt bar de la home,
// el container "explota" a este panel que ocupa todo el viewport
// (excepto la top bar mobile y la sidebar desktop). Adentro hay un chat
// mixto: mensajes + tool UI cards inline que el agente va mostrando una
// tras otra (pick_product → pick_pose → confirm_generate).
//
// Patrón: useV2Chat maneja la lógica; este componente solo renderiza.

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { sonarListo } from '../../utils/sonarListo';
import { X, ArrowUp, ArrowUpFromLine, Check, Upload, Package, Sparkles, ChevronRight, ChevronLeft, Plus, UserPlus, Mic, Brain, Download, Play, Search, Square, PenLine, Eye, FileSpreadsheet, FileText, BookmarkPlus, Film, Music, SlidersHorizontal, Instagram, Megaphone } from 'lucide-react';
import { CumbreMascot } from '../../components/CumbreMascot';
import type { TipoDeActividad } from './actividadDelAgente';
import { GenerandoAudioEnCard, GenerandoEnCard } from './GenerandoEnCard';
import { DURACION_BARRA_VIDEO_MS } from './recuadrosEnCard';
import { supabase } from '../../lib/supabase';
import { TRANSFORM_MINIATURA_PARA_IDENTIFICAR } from '../../utils/firmaDeStorage';
import { useSidebar } from '../../context/SidebarContext';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../utils/preloadGeneratedImages';
import {
  CONFIG_OPTIONS_V2,
  type MockProductV2,
} from './mockData';
import { useUserProducts, useUserProductsLoaded,
  ProductEditContext,
} from './productsContext';
import { limitePedido, tipoPedido } from './mostrarGuardado';
import { AgregarALanzamientoCard } from '../lanzamientos/AgregarALanzamientoCard';
import { EditarLanzamientoCard } from '../lanzamientos/EditarLanzamientoCard';
import { CrearPdfCard } from './pdfs/CrearPdfCard';
import { CrearPlanillaCard } from './planillas/CrearPlanillaCard';
import { MostrarGuardadoCard } from './MostrarGuardadoCard';
import { fetchProductVistas, getProductFullImageUrl, type FotoDeProducto } from './productsApi';
import { getLastUsedProductIds } from './lastUsedProducts';
import {
  applyProductCatalogFilter,
  DEFAULT_PRODUCT_SORT,
  MIN_PRODUCTS_FOR_TOOLBAR,
  type ProductCatalogSort,
} from './productCatalogFilter';
import {
  ProductCatalogNoResults,
  ProductCatalogToolbar,
} from './components/ProductCatalogToolbar';
import { PreguntasCard } from './components/PreguntasCard';
import { PickCreacionCard } from './components/PickCreacionCard';
import { useUserModels } from './modelsContext';
import { useAgentEnv } from './agentEnvContext';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { SubirProductoDrawer } from './components/SubirProductoDrawer';
import { AddOptionCard } from './components/AddOptionCard';
import { AnimatedBlurBg } from './AnimatedBlurBg';
import {
  CreateView,
  UploadFlow,
  ModelPickerSheet,
  type ModelSelection,
} from './ModelPicker';
import { fotoDeModeloParaReferencia, type ModelV2 } from './modelsApi';
import { conReferenciasFrescas } from '../../utils/firmasFrescas';
import { fetchAllPoses, type PoseV2 } from './posesApi';
import { PosePickerSheet } from './PosePicker';
import { FormatPicker } from './FormatPicker';
import { CalidadImagenSheet } from './components/CalidadImagenSheet';
import { BotonGenerarSinPreguntar } from './components/BotonGenerarSinPreguntar';
import {
  creditosPorResolucion,
  labelResolucion,
  normalizarResolucion,
  type ResolucionImagen,
} from '../../lib/resolucionImagen';
import {
  FondoPickerSheet,
  fondoSelectionLabel,
  type FondoSelection,
} from './FondoPicker';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';
import type { AgentBackground } from './agentGenerate';
import { useProveedorDeImagen } from './proveedorDeImagenContext';
import { fetchBrandContext } from './agentMemoryApi';
import CachedImg from '../../components/CachedImg';
import { signReferenceImage, type ChatAttachment } from './agentAttachments';
import { adjuntaQueFalta, adjuntoPorId, elegirVideoAdjuntado } from './adjuntosDelMensaje';
import { duracionConVideoDeReferencia } from './duracionConVideoDeReferencia';
import { pideProfesional } from '../../utils/videoDeReferencia';
import { limpiarIdsDelPrompt } from './limpiarIdsDelPrompt';
import { useMiniaturasDeReferencias, type SpecDeReferencia } from './miniaturasDeReferencias';
import { ChipsDeReferencias } from './components/ChipsDeReferencias';
import { CantidadInline } from './modes/asistente/ControlesGeneracion';
import { duracionDeVideoPorUrl } from '../../utils/duracionDeVideoPorUrl';
import { mapErrorTypeToMessage, type GenerateAdErrorType } from '../../lib/generateAdRequest';
import ProductGridSkeleton from '../../components/ProductGridSkeleton';
import { renderMarkdown } from '../../utils/chatMarkdown';
import { marcaParaFiltrar, orDeMarca } from '../../utils/filtroDeMarca';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import { getFormatLabel } from './mockData';
import {
  creditsForImage,
  creditsForSong,
  creditsForEfecto,
  creditsForVideoGeneral,
  creditsForVoice,
  safeResolution,
  videoGeneralRatePerSec,
  VIDEO_BASICA_MAX_S,
  VIDEO_PROFESIONAL_MAX_S,
} from '../../utils/credits';
import { AYUDA_BORRADOR, borradorDe, borradorVigente, conFinal, creditosDePasarA1080p, esBorrador, RESOLUCIONES_VIDEO, ROTULO_PASAR_A_1080P } from '../../lib/resolucionVideo';
import { pasarBorradorA1080p } from '../../lib/videoBorrador';
import { estimarDuracionVideoMs } from '../../utils/estimacionVideo';
import { PasarA1080pDialogo } from '../../components/PasarA1080pDialogo';
import { VideoConfigSheet } from './components/VideoConfigSheet';
import { PromptRecientesButton } from './components/PromptRecientesButton';
import { useExitTransition } from '../../hooks/useExitTransition';
import {
  type UseV2ChatReturn,
  type V2ChatMessage,
  type V2ToolCard,
  isInlinePendingCard,
} from './useV2Chat';
import { DURACION_POR_DEFECTO, esFormatoVideo } from '../../chat-atlas/preferenciasDeGeneracion';
import {
  MAX_REFERENCIAS_VIDEO,
  etiquetarAdjuntas,
  priorizarReferencias,
  elegirReferenciasDeVideo,
  MENSAJE_MODELO_SIN_FOTO,
  ReferenciaIrrecuperable,
  referenciaDeLogo,
  referenciaDeProducto,
  type ReferenciaEtiquetada,
} from './referenciasDeGeneracion';
import { urlDeFondoGuardado, urlDePoseGuardada } from './referenciasDelCatalogo';
// Trabajo en lote (ADR-2026-09-08-el-chat-trabaja-en-lote). Viven en su carpeta y no
// importan nada de acá: sin ciclo.
import { LoteImagenesCard } from './lotes/LoteImagenesCard';
import { useVisorDelLienzo } from './visorDelLienzo';
import { liberarLanzamiento, reservarLanzamiento } from './lotes/lanzamientoUnico';
import { AgregarProductosCard } from './lotes/AgregarProductosCard';
import { AdjuntosDelUsuario } from './components/AdjuntosDelUsuario';
import { palabraDeModeloActual, usePalabraDeModelo } from '../../lib/nombreDeModelo';
import { MercadoLibreIcon } from '../../components/MercadoLibreIcon';

// ─── Hook para cargar poses (compartido por PickPoseCard) ──────────────
// Usa `fetchAllPoses` (PoseV2) — la MISMA fuente que el menú real de poses
// (PosePickerSheet) usa en el modo modelo / plantillas. Así el agente y el
// resto comparten datos y UI de poses (una sola fuente de verdad).
const usePoses = () => {
  const [poses, setPoses] = useState<PoseV2[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAllPoses()
      .then((p) => {
        if (alive) setPoses(p);
      })
      .catch((err) => console.error('[ChatPanelV2] fetch poses failed', err))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { poses, loading };
};

// ─── Hook para cargar fondos (biblioteca global + guardados del user) ──
// Lo usan PickBackgroundCard y ConfirmGenerateCard (para resolver el id
// horneado por el agente a nombre/fileName). Biblioteca sin filtro de rubro
// acá (los ids son globales; el filtrado por rubro vive en el prompt).
const useFondos = () => {
  const { companyId } = useAgentEnv();
  const [biblioteca, setBiblioteca] = useState<FondoBiblioteca[]>([]);
  const [guardados, setGuardados] = useState<FondoGuardado[]>([]);

  const reload = React.useCallback(() => {
    Promise.all([
      fetchBibliotecaFondos(null),
      companyId ? fetchFondosGuardados(companyId) : Promise.resolve([]),
    ])
      .then(([bib, guard]) => {
        setBiblioteca(bib);
        setGuardados(guard);
      })
      .catch((err) => console.error('[ChatPanelV2] fetch fondos failed', err));
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { biblioteca, guardados, reload };
};

// Mapea el `background`/`suggested` que pasa el agente (mode + id?/text?) a una
// FondoSelection completa (resolviendo nombre/fileName/url de las listas).
const agentBgToFondoSelection = (
  bg: { mode?: string; id?: number | string; text?: string } | null | undefined,
  biblioteca: FondoBiblioteca[],
  guardados: FondoGuardado[],
): FondoSelection | null => {
  if (!bg || !bg.mode) return null;
  if (bg.mode === 'auto') return { mode: 'auto' };
  if (bg.mode === 'blanco') return { mode: 'blanco' };
  if (bg.mode === 'descripcion')
    return { mode: 'descripcion', text: String(bg.text || '') };
  if (bg.mode === 'biblioteca') {
    const id = Number(bg.id);
    const f = biblioteca.find((x) => x.id === id);
    return { mode: 'biblioteca', id, name: f?.nombre || 'Fondo', imageUrl: f?.imageUrl || '' };
  }
  if (bg.mode === 'guardado') {
    const id = Number(bg.id);
    const f = guardados.find((x) => x.id === id);
    if (!f) return null; // sin fileName no podemos generar → cae a automático
    return { mode: 'guardado', fileName: f.fileName, name: f.nombre, imageUrl: f.imageUrl };
  }
  return null;
};

// Mapea una FondoSelection (de pick_background o del baked) al AgentBackground
// que entiende generateModeloImage.
const fondoSelectionToBackground = (
  sel: FondoSelection | null,
): AgentBackground | null => {
  if (!sel) return null;
  if (sel.mode === 'auto') return { mode: 'auto' };
  if (sel.mode === 'blanco') return { mode: 'blanco' };
  if (sel.mode === 'biblioteca') return { mode: 'biblioteca', id: sel.id };
  if (sel.mode === 'guardado') return { mode: 'guardado', fileName: sel.fileName };
  return { mode: 'descripcion', text: sel.text };
};

// ─── ChatPanelV2 ──────────────────────────────────────────────────────

interface ChatPanelV2Props {
  chat: UseV2ChatReturn;
  open: boolean;
  onClose: () => void;
}

export const ChatPanelV2: React.FC<ChatPanelV2Props> = ({
  chat,
  open,
  onClose,
}) => {
  const { sidebarWidth } = useSidebar();
  const [animated, setAnimated] = useState(false);
  // Alto (px) que el teclado tapa desde abajo (medido con visualViewport).
  // Achicamos el panel desde el bottom con esto → el input queda arriba del
  // teclado en iOS (donde el teclado NO achica el layout viewport).
  const [kbInset, setKbInset] = useState(0);
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Entrada animada cuando se abre.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setAnimated(true));
    } else {
      setAnimated(false);
    }
  }, [open]);

  // Bloquear scroll de fondo mientras el chat está abierto.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Auto-scroll al fondo cuando llega contenido nuevo. Solo si el user ya
  // estaba cerca del fondo (no interrumpir si está leyendo arriba).
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 120) {
      sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
    }
  }, [chat.messages, chat.activeToolCard]);

  // Auto-resize del textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // Teclado (iOS): el WebView NO achica el layout viewport al abrir el teclado
  // (lo tapa por encima), así que el input `fixed` de abajo quedaba detrás del
  // teclado e iOS scrolleaba raro para mostrarlo. Con `visualViewport` medimos
  // cuánto tapa el teclado y achicamos el panel desde el bottom → el input flota
  // justo arriba del teclado, sin el scroll saltón.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onVV = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Umbral: solo reaccionamos al teclado (>120px), no a las barras del
      // navegador que aparecen/desaparecen (insets chicos).
      setKbInset(inset > 120 ? inset : 0);
    };
    onVV();
    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    return () => {
      vv.removeEventListener('resize', onVV);
      vv.removeEventListener('scroll', onVV);
      setKbInset(0);
    };
  }, [open]);

  const handleSubmit = () => {
    if (!input.trim() || chat.streaming || chat.activeToolCard) return;
    chat.sendMessage(input.trim());
    setInput('');
  };

  // Para el morph audio↔enviar del botón derecho del input bar.
  const hasText = input.trim().length > 0;

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-[105] bg-surface-mobile md:bg-surface-content md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden flex flex-col transition-all duration-350 ease-out ${
        animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        // Teclado (iOS): achicamos el panel desde abajo lo que tapa el teclado.
        // 0 = sin teclado → cae al `bottom: 0` de la clase `inset-0`.
        bottom: kbInset || undefined,
        // En desktop, no cubrimos la sidebar — quedamos a la derecha.
        // En mobile, ocupamos todo el ancho.
        left: typeof window !== 'undefined' && window.innerWidth >= 768
          ? sidebarWidth
          : 0,
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="shrink-0 h-14 px-4 sm:px-6 flex items-center justify-between border-b border-black/[0.06] bg-white/95 backdrop-blur-sm">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full text-brand-black/70 hover:bg-black/[0.04] active:bg-black/[0.06] transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-[20px] h-[20px]" />
        </button>
        <p className="text-[13.5px] text-[#3C3C43]/55 font-medium">
          Generando con Atlas
        </p>
        <div className="w-9 h-9" />
      </header>

      {/* ── Messages ──────────────────────────────────────────── */}
      {/* Solo renderizamos las tool cards RESUELTAS inline en el chat (como
          resumen compacto). La activa se muestra anclada abajo, en el slot
          del input bar — igual que activeAsk en el Director de Estudio. */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-6"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="max-w-3xl mx-auto flex flex-col gap-5">
          {chat.messages.map((msg, i) => (
            <MessageRow key={i} msg={msg} chat={chat} />
          ))}
          {chat.streaming && !chat.messages.some((m) => m.role === 'assistant' && m.text) && (
            <TypingDots />
          )}
        </div>
      </div>

      {/* ── Bottom slot: tool card activa O input bar ──────────────── */}
      {/* Sin divisor horizontal — el input flota con un shadow sutil, igual
          al patrón del Director de Estudio. */}
      <div
        className="shrink-0 bg-white px-4 sm:px-6 pt-3 pb-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className="max-w-3xl mx-auto">
          {chat.activeToolCard ? (
            <div
              key={chat.activeToolCard.id}
              className="animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]"
              style={{ maxHeight: '65dvh' }}
            >
              <ToolCardRenderer
                card={chat.activeToolCard}
                chat={chat}
                embedded
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] focus-within:ring-brand-primary/25 focus-within:shadow-[0_4px_18px_-6px_rgba(22,68,235,0.18),0_1px_3px_rgba(0,0,0,0.05)] transition-[box-shadow,--tw-ring-color] duration-200">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Pedile algo a Atlas…"
                rows={1}
                disabled={chat.streaming}
                className="w-full resize-none bg-transparent outline-none px-4 pt-5 text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-snug"
              />
              {/* Bottom row con + (adjuntar, mockup) izq y audio/enviar der.
                  Mismo patrón que el prompt bar de la home — el botón der
                  muta entre Mic (sin texto) y ArrowUp (con texto) con un
                  cross-fade de opacity. */}
              <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-3">
                <button
                  type="button"
                  aria-label="Adjuntar"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black/70 transition-colors"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.25} />
                </button>
                {chat.streaming ? (
                  // Turno en curso → botón Detener (escape hatch si el
                  // stream se colgó; también corta un turno largo a propósito).
                  <button
                    type="button"
                    onClick={chat.stopTurn}
                    aria-label="Detener"
                    // Fondo SÓLIDO — ver nota en AsistenteMode / ADR-140:
                    // `bg-brand-black/85` sobre un var()+hex genera CSS
                    // inválido y el botón queda invisible salvo en hover.
                    className="relative inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm bg-brand-black hover:opacity-90 text-white transition-opacity duration-200"
                  >
                    <Square className="w-3 h-3 fill-current" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={hasText ? handleSubmit : undefined}
                    aria-label={hasText ? 'Enviar' : 'Grabar audio'}
                    className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 ${
                      hasText
                        ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                        : 'bg-neutral-100 hover:bg-neutral-200 text-brand-black/70'
                    }`}
                  >
                    <Mic
                      className={`absolute w-4 h-4 transition-opacity duration-200 ${
                        hasText ? 'opacity-0' : 'opacity-100'
                      }`}
                    />
                    <ArrowUp
                      className={`absolute w-4 h-4 transition-opacity duration-200 ${
                        hasText ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Message row ───────────────────────────────────────────────────────

export const MessageRow: React.FC<{
  msg: V2ChatMessage;
  chat: UseV2ChatReturn;
  /** Ref al mensaje del user más reciente — para anclarlo arriba al scrollear. */
  userRef?: React.Ref<HTMLDivElement>;
  /** Si true, las cards PENDIENTES también se renderizan inline, como un
   *  mensaje más del chat (el modo asistente). Por default quedan afuera:
   *  el panel lateral de InterfazV2 las muestra en su propio slot al pie y
   *  con esto en true saldrían duplicadas. */
  showPendingCards?: boolean;
}> = ({ msg, chat, userRef, showPendingCards = false }) => {
  if (msg.role === 'user') {
    return (
      <div
        ref={userRef}
        className="flex flex-col items-end mt-6 first:mt-0 animate-mode-swap"
      >
        {/* Lo que adjuntó, arriba de su texto: fotos, archivos, producto, modelo, lote
            (ADR-2026-09-08-agregar-productos-es-una-herramienta).

            Las que son foto se tocan y se abren en grande, con "Agregar al chat" como
            único botón (ADR-2026-09-14-la-foto-del-mensaje-se-vuelve-a-adjuntar). Va el
            `addAttachment` del MISMO motor que dibuja el mensaje, así la foto vuelve a la
            conversación en la que está. */}
        {msg.adjuntos && msg.adjuntos.length > 0 && (
          <AdjuntosDelUsuario
            adjuntos={msg.adjuntos}
            onAgregarAdjunto={chat.addAttachment}
          />
        )}
        {msg.text && (
          <div className="max-w-[80%] bg-[#F2F2F7] rounded-[18px] rounded-tr-[6px] px-4 py-2.5 text-[15px] text-brand-black/90 leading-snug whitespace-pre-wrap">
            {msg.text}
          </div>
        )}
      </div>
    );
  }

  // Mensaje "upgrade": container de mejorar plan (mismo modal de créditos
  // agotados de la app), en vez de texto.
  if (msg.kind === 'upgrade') {
    return <UpgradeInlineCard />;
  }

  // Assistant: solo renderizamos cards RESUELTAS inline (las pending viven
  // en el slot bottom). Si la única card del mensaje está pending, igual
  // mostramos el texto.
  // Con `showPendingCards`, las PROPUESTAS de generación se renderizan acá
  // aunque estén pending o cancelled — así son un mensaje más del chat y no
  // desaparecen al tocar Detener (que las marca 'cancelled').
  const inlineCards =
    msg.toolCards?.filter((c) =>
      c.status === 'resolved' ||
      (showPendingCards && isInlinePendingCard(c)),
    ) || [];
  return (
    <div className="flex flex-col gap-3 animate-mode-swap">
      {msg.text && (
        <div className="text-[15px] text-brand-black/85 leading-relaxed [&_a]:text-brand-primary">
          {renderMarkdown(msg.text)}
        </div>
      )}
      {inlineCards.map((card) => (
        <ToolCardRenderer key={card.id} card={card} chat={chat} />
      ))}
    </div>
  );
};

// ─── Upgrade inline card ───────────────────────────────────────────────
// Container en el chat para cuando el usuario se queda sin créditos. El botón
// abre el MISMO SubscriptionLimitModal que el resto de la app (límite de plan).
const UpgradeInlineCard: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="animate-mode-swap rounded-2xl border border-brand-primary/20 bg-gradient-to-br from-brand-primary/[0.07] to-brand-primary/[0.02] p-4 max-w-[420px]">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary shrink-0">
          <Sparkles className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-brand-black">
            Te quedaste sin créditos
          </p>
          <p className="text-[13.5px] text-brand-black/60 mt-0.5 leading-snug">
            Mejorá tu plan para seguir creando con el asistente.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 w-full py-2.5 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-sm transition-colors"
      >
        Mejorar plan
      </button>
      <SubscriptionLimitModal
        isOpen={open}
        onClose={() => setOpen(false)}
        type="plan-limit-reached"
      />
    </div>
  );
};

// ─── Typing dots ───────────────────────────────────────────────────────

export const TypingDots: React.FC = () => (
  <div className="flex items-center gap-1.5 py-1">
    <span className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce" style={{ animationDelay: '0ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce" style={{ animationDelay: '150ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-brand-black/30 animate-bounce" style={{ animationDelay: '300ms' }} />
  </div>
);

// ─── Thinking indicator ────────────────────────────────────────────────
// Indicador de "Pensando…" mientras el agente prepara una respuesta o una
// card. Se muestra entre turnos, no solo en el primero.
//
// Dos looks, y `mascota` elige:
//   • Por defecto (v1): ícono Brain gris + shimmer gris — el mismo espíritu
//     que el "Pensando…" del Director de Estudio.
//   • `mascota` (v2): Cumbre en su estado `thinking` + el texto en el gradiente
//     de marca. Es el ÚNICO momento colorido del chat, a propósito: el resto de
//     la conversación es texto negro sobre blanco y la espera es lo que necesita
//     decir "acá está pasando algo" — ADR-2026-09-08-la-espera-es-el-momento-colorido.

/** El ícono de cada tipo de actividad (ADR-2026-09-09-el-chat-dice-que-esta-haciendo):
 *  el texto dice qué hace; el ícono lo hace reconocible de un vistazo. */
const ICONO_DE_ACTIVIDAD: Record<TipoDeActividad, React.FC<{ className?: string }>> = {
  pensar: Brain,
  escribir: PenLine,
  mirar: Eye,
  planilla: FileSpreadsheet,
  pdf: FileText,
  buscar: Search,
  guardar: BookmarkPlus,
  video: Film,
  audio: Music,
  catalogo: Package,
  pantalla: SlidersHorizontal,
};

export const ThinkingIndicator: React.FC<{
  label?: string;
  tipo?: TipoDeActividad;
  /** Cumbre + gradiente de marca en vez del ícono gris. Sólo v2. */
  mascota?: boolean;
}> = ({ label = 'Pensando…', tipo = 'pensar', mascota = false }) => {
  const Icono = ICONO_DE_ACTIVIDAD[tipo] ?? Brain;
  const shimmer = (
    <span
      className="text-[15px] font-semibold bg-clip-text text-transparent"
      style={{
        // Mismo mecanismo en los dos casos (gradiente de 200% que se desplaza);
        // lo único que cambia son los colores. Los extremos del gradiente son
        // iguales para que el loop no tenga corte.
        backgroundImage: mascota
          ? 'linear-gradient(90deg, #1644eb 25%, #60e7f0 50%, #1644eb 75%)'
          : 'linear-gradient(90deg, rgba(60,60,67,0.35) 25%, rgba(60,60,67,0.85) 50%, rgba(60,60,67,0.35) 75%)',
        backgroundSize: '200% 100%',
        animation: 'atlasThinkShimmer 1.4s linear infinite',
      }}
    >
      {label}
    </span>
  );

  const keyframes = (
    <style>{`@keyframes atlasThinkShimmer { to { background-position: -200% 0 } }`}</style>
  );

  // Sin mascota (v1 y el Chat): ícono del TIPO de actividad + shimmer gris.
  if (!mascota) {
    return (
      <div className="flex items-center gap-2.5 py-1">
        {keyframes}
        <Icono className="w-4 h-4 shrink-0 text-[#3C3C43]/45 animate-pulse" />
        {shimmer}
      </div>
    );
  }

  // Con mascota son DOS renglones y no uno: Cumbre cruza la fila entera de punta
  // a punta (`.cumbre-viaje`), así que no puede compartir la línea con el texto
  // — le pasaría por encima. El texto va abajo, alineado a la izquierda con el
  // resto de la conversación.
  //
  // La pista es `relative` + alto fijo: el wrapper que cruza es `absolute`, o sea
  // que no aporta alto, y sin el `h-` la fila colapsaría a 0.
  //   • `w-full` → "todo el espacio disponible de la columna" es literalmente el
  //     ancho de la columna de mensajes, sin números.
  //   • `overflow-visible` → la sombra de la mascota se sale de la caja a
  //     propósito; sin esto se recorta contra los bordes en cada extremo.
  return (
    <div className="w-full py-1">
      {keyframes}
      <div className="relative h-[34px] w-full overflow-visible">
        {/* TRES capas, una animación cada una — `transform` es una sola propiedad
            y dos animaciones sobre ella no se suman (gana la última):
              .cumbre-viaje  cruza la fila   (translateX)
              .cumbre-giro   da la vuelta    (rotate)
              .cumbre        flota           (translateY)
            Ver `cumbre.css`. */}
        <span className="cumbre-viaje">
          <span className="cumbre-giro">
            {/* `cumbre--mini`: el float de 14px y la sombra de 60px de `.cumbre`
                están calculados para los 120px del empty state; a 34px, en una
                fila de texto, la harían saltar media línea. */}
            <CumbreMascot
              state="thinking"
              size={34}
              decorativa
              className="cumbre--mini w-[34px] h-auto"
            />
          </span>
        </span>
      </div>
      <div className="mt-1.5">{shimmer}</div>
    </div>
  );
};

// ─── Tool card renderer ────────────────────────────────────────────────

export const ToolCardRenderer: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
  /** Si true, el card ya está envuelto por fuera (slot bottom) — saltea el wrapper. */
  embedded?: boolean;
}> = ({ card, chat, embedded }) => {
  // Lo reporta la card hija (ver `onCargandoChange`): "está generando" es
  // `useState` de adentro, no algo derivable de `card` — a diferencia de
  // `soloImagenes`, que sí sale de `status` + `response`.
  const [cargando, setCargando] = useState(false);
  // Una card suelta (`detached`) ya no es la activa del chat, pero sigue
  // siendo operable: su botón Generar exige `active`, y el sentido de dejarla
  // en el stream es justamente poder volver y generarla.
  const isActive = chat.activeToolCard?.id === card.id || !!card.detached;
  // Las cards pasadas se atenúan como cue de "paso terminado" — EXCEPTO las
  // resueltas que muestran un RESULTADO (imágenes/video/audio generados):
  // el resultado es el entregable, no puede verse lavado.
  const showsResult =
    card.status === 'resolved' &&
    (card.kind === 'confirm_generate' ||
      card.kind === 'generate_image' ||
      card.kind === 'generate_video' ||
      card.kind === 'generate_audio' ||
      card.kind === 'show_recent_media' ||
      card.kind === 'mostrar_guardado' ||
      card.kind === 'crear_planilla' ||
      card.kind === 'crear_pdf' ||
      card.kind === 'editar_lanzamiento' ||
      card.kind === 'agregar_a_lanzamiento' ||
      card.kind === 'contact_support' ||
      // Su botón sigue sirviendo después: la persona puede volver y tocarlo.
      card.kind === 'conectar_tienda' ||
      card.kind === 'conectar_instagram' ||
      card.kind === 'conectar_anuncios' ||
      card.kind === 'conectar_mercadolibre' ||
      card.kind === 'generar_lote_imagenes' ||
      card.kind === 'agregar_productos');
  const dimmed = card.status !== 'pending' && !showsResult;
  // ¿Esta card ya es SOLO imágenes? Entonces el recuadro blanco se achica a su
  // medida (`fit`) en vez de ocupar la columna entera — no es un panel, es el
  // marco de una foto (ADR-2026-09-08-el-resultado-es-solo-la-imagen).
  //
  // Esto SÍ se puede saber acá, a diferencia de "está generando": `status` y
  // `response` viven en la card; `generating` es `useState` adentro del hijo.
  const soloImagenes =
    card.status === 'resolved' &&
    (card.kind === 'confirm_generate' || card.kind === 'generate_image') &&
    ((card.response?.image_ids as number[] | undefined)?.length ?? 0) > 0;
  // El recuadro blanco se achica en los DOS momentos en que la card es puro
  // recuadro de imagen: mientras espera y cuando ya está el resultado.
  const ajustarAlContenido = cargando || soloImagenes;

  // Wrapper común para cuando vive inline en el chat. Cuando va embedded
  // (slot bottom) no envolvemos: el contenedor de afuera ya da el chrome.
  const wrap = (children: React.ReactNode, fit = false) => {
    if (embedded) return <>{children}</>;
    return (
      <div
        className={`rounded-2xl bg-white border border-black/[0.06] shadow-sm overflow-hidden transition-opacity ${
          fit ? 'w-fit max-w-full' : ''
        } ${dimmed ? 'opacity-70' : ''}`}
      >
        {children}
      </div>
    );
  };

  if (card.kind === 'pick_product') {
    return wrap(
      <PickProductCard
        card={card}
        active={isActive}
        onResolve={(ids) => chat.resolveToolCard(card.id, { product_ids: ids })}
      />,
    );
  }
  if (card.kind === 'pick_model') {
    return wrap(
      <PickModelCard
        card={card}
        active={isActive}
        onResolve={(payload) => chat.resolveToolCard(card.id, payload)}
      />,
    );
  }
  if (card.kind === 'pick_pose') {
    return wrap(
      <PickPoseCard
        card={card}
        active={isActive}
        onResolve={(poseIds, poseNames) =>
          chat.resolveToolCard(card.id, {
            pose_ids: poseIds,
            pose_names: poseNames,
          })
        }
      />,
    );
  }
  if (card.kind === 'pick_format') {
    return wrap(
      <PickFormatCard
        card={card}
        active={isActive}
        onResolve={(format) => {
          try {
            localStorage.setItem('atlas-v2-last-format', format);
          } catch {
            /* noop */
          }
          chat.resolveToolCard(card.id, { format });
        }}
      />,
    );
  }
  if (card.kind === 'pick_background') {
    return wrap(
      <PickBackgroundCard
        card={card}
        active={isActive}
        onResolve={(fondo) => chat.resolveToolCard(card.id, { fondo })}
      />,
    );
  }
  if (card.kind === 'confirm_generate') {
    return wrap(
      <ConfirmGenerateCard
        card={card}
        active={isActive}
        chat={chat}
        messages={chat.messages}
        onCargandoChange={setCargando}
      />,
      ajustarAlContenido,
    );
  }
  if (card.kind === 'pick_creacion') {
    return wrap(
      <PickCreacionCard
        card={card}
        active={isActive}
        onResolve={(r) => chat.resolveToolCard(card.id, r)}
      />,
    );
  }
  if (card.kind === 'preguntar') {
    return wrap(
      <PreguntasCard
        card={card}
        active={isActive}
        onResolve={(respuestas) =>
          chat.resolveToolCard(card.id, { respuestas })
        }
      />,
    );
  }
  if (card.kind === 'confirm_research') {
    return wrap(
      <ConfirmResearchCard
        card={card}
        active={isActive}
        onResolve={(accepted) =>
          chat.resolveToolCard(card.id, { accepted })
        }
        noPreguntarMas={chat.noPreguntarMas}
      />,
    );
  }
  if (card.kind === 'generate_image') {
    return wrap(
      <FreeformGenerateCard
        card={card}
        active={isActive}
        chat={chat}
        onCargandoChange={setCargando}
      />,
      ajustarAlContenido,
    );
  }
  if (card.kind === 'generate_audio') {
    return wrap(
      <MediaGenerateCard
        card={card}
        active={isActive}
        chat={chat}
        mediaType="audio"
      />,
    );
  }
  if (card.kind === 'generate_video') {
    return wrap(
      <MediaGenerateCard
        card={card}
        active={isActive}
        chat={chat}
        mediaType="video"
      />,
      true, // el container abraza el ancho del video (sin márgenes vacíos)
    );
  }
  if (card.kind === 'show_recent_media') {
    return wrap(<RecentMediaCard card={card} chat={chat} />);
  }
  if (card.kind === 'mostrar_guardado') {
    // OJO: acá NO se puede devolver `null` por un input inválido. La card es la que
    // resuelve el turno al montar; sin card, el `await` del motor no vuelve nunca y el
    // chat se queda "pensando" (ADR-2026-09-08-las-cards-de-mostrar-tienen-que-montar).
    // Un tipo que no existe lo maneja la card adentro: se resuelve con el motivo.
    return wrap(
      <MostrarGuardadoCard
        card={card}
        chat={chat}
        tipo={tipoPedido(card.input)}
        limite={limitePedido(card.input)}
      />,
    );
  }
  if (card.kind === 'editar_lanzamiento') {
    return wrap(<EditarLanzamientoCard card={card} active={isActive} chat={chat} />);
  }
  if (card.kind === 'agregar_a_lanzamiento') {
    return wrap(<AgregarALanzamientoCard card={card} active={isActive} chat={chat} />);
  }
  if (card.kind === 'crear_pdf') {
    return wrap(<CrearPdfCard card={card} chat={chat} />);
  }
  if (card.kind === 'crear_planilla') {
    return wrap(<CrearPlanillaCard card={card} chat={chat} />);
  }
  if (card.kind === 'contact_support') {
    return wrap(<ContactSupportCard card={card} chat={chat} />);
  }
  if (card.kind === 'conectar_tienda') {
    return wrap(<ConectarTiendaCard card={card} chat={chat} />);
  }
  if (card.kind === 'conectar_instagram') {
    return wrap(<ConectarInstagramCard card={card} chat={chat} />, true);
  }
  if (card.kind === 'conectar_anuncios') {
    return wrap(<ConectarAnunciosCard card={card} chat={chat} />, true);
  }
  if (card.kind === 'conectar_mercadolibre') {
    return wrap(<ConectarMercadoLibreCard card={card} chat={chat} />, true);
  }
  if (card.kind === 'generar_lote_imagenes') {
    return wrap(<LoteImagenesCard card={card} active={isActive} chat={chat} />);
  }
  if (card.kind === 'agregar_productos') {
    return wrap(<AgregarProductosCard card={card} active={isActive} chat={chat} />);
  }
  return null;
};

// ─── PickProductCard ───────────────────────────────────────────────────
// Tiene 2 modos según si el agente preseleccionó algo:
//
//   • Con preselección — muestra las preseleccionadas como cards en un
//     mini grid (toggle add/remove), debajo dos botones secundarios:
//     "Subir imagen" + "Ver todos" (este abre el menú fullscreen). El
//     Confirmar aparece pegado al bottom del card cuando hay al menos
//     una opción seleccionada.
//
//   • Sin preselección — solo dos botones grandes: "Subir imagen" + "Ver
//     mis productos" (este último abre el menú fullscreen con todo el
//     catálogo). No hay Confirmar acá; se confirma desde el menú.

// ─── Alta de producto desde el chat ───────────────────────────────────
// ADR-2026-09-19-el-picker-de-productos-deja-subir-uno-nuevo, extraído a un hook por
// ADR-2026-09-25-subir-nuevo-producto-en-la-tarjeta-del-agente: lo usan el catálogo del
// chat (`ProductFullMenu`, su tarjeta "Agregar producto") y la tarjeta de elegir producto
// (`PickProductCard`, su botón "Subir nuevo producto", que hasta ahora no hacía nada).
//
// El alta es el mismo drawer del catálogo (`SubirProductoDrawer`); lo que necesita sale de
// los contextos que ya proveen `InterfazV2` y el entorno del Agente (empresa + refresco del
// catálogo) y del perfil de marca (marca activa y rubro), así ningún host cablea nada.
//
// `onNuevos` recibe los ids de lo que apareció en el catálogo DESPUÉS de un alta que
// avisó que subió algo.
function useAltaDeProductoDelChat(onNuevos: (ids: string[]) => void): {
  abrir: () => void;
  drawer: React.ReactNode;
} {
  const rawProducts = useUserProducts();
  const { companyId: companyIdEdicion, refreshProducts } = useContext(ProductEditContext);
  const { currentBrandId, activeBrand, profile: perfilMarca } = useBrandProfile();
  const { plan: planDeLaEmpresa } = useAgentEnv();
  const [subirAbierto, setSubirAbierto] = useState(false);
  // Foto del catálogo al abrir el alta: lo que aparezca después es lo recién subido.
  const idsAntesDeSubirRef = useRef<Set<string> | null>(null);
  /* La foto sola no alcanza para disparar: hace falta que el alta AVISE que subió algo —
     ADR-2026-09-21-la-foto-del-catalogo-se-desarma-al-cancelar-el-alta.

     Con la foto como única condición, cancelar el alta la dejaba viva y el PRÓXIMO
     producto que apareciera por cualquier motivo (un compañero que sube uno, un refresco
     demorado que llega tarde) se seleccionaba solo — y en modo de selección única encima
     REEMPLAZABA lo que el usuario tenía elegido. */
  const huboAltaRef = useRef(false);
  // La última versión del callback, sin volver a correr el efecto cuando cambia.
  const onNuevosRef = useRef(onNuevos);
  onNuevosRef.current = onNuevos;
  useEffect(() => {
    const antes = idsAntesDeSubirRef.current;
    if (!antes || !huboAltaRef.current) return;
    const nuevos = rawProducts.map((p) => p.id).filter((id) => !antes.has(id));
    if (nuevos.length === 0) return;
    idsAntesDeSubirRef.current = null;
    huboAltaRef.current = false;
    onNuevosRef.current(nuevos);
  }, [rawProducts]);

  const abrir = () => {
    idsAntesDeSubirRef.current = new Set(rawProducts.map((p) => p.id));
    setSubirAbierto(true);
  };

  const drawer = (
    <SubirProductoDrawer
      open={subirAbierto}
      onClose={() => {
        setSubirAbierto(false);
        /* Cerrar SIN haber subido nada desarma la foto. Si el alta ya avisó, la foto
           se conserva: el drawer puede cerrarse antes de que llegue el catálogo
           nuevo, y ahí el efecto todavía tiene que poder seleccionar lo subido. */
        if (!huboAltaRef.current) idsAntesDeSubirRef.current = null;
      }}
      userCompanyId={companyIdEdicion}
      brandId={currentBrandId}
      rubro={activeBrand?.rubro ?? perfilMarca?.rubro ?? null}
      userPlan={planDeLaEmpresa ?? null}
      onProductsRefreshed={() => {
        // Arma el efecto: a partir de acá, lo que aparezca en el catálogo es lo que
        // este usuario acaba de subir.
        huboAltaRef.current = true;
        void refreshProducts();
      }}
    />
  );

  return { abrir, drawer };
}

const PickProductCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (productIds: string[]) => void;
}> = ({ card, active, onResolve }) => {
  const userProducts = useUserProducts();
  const { message, preselected_ids, candidate_ids, allow_multi } =
    card.input as {
      message: string;
      preselected_ids?: string[];
      candidate_ids?: string[];
      allow_multi?: boolean;
    };
  const allowMulti = allow_multi !== false; // default true
  // `preselected_ids` arrancan TILDADOS (match único claro). `candidate_ids`
  // se muestran en el grid SIN tildar (ej: el user dijo "el vestido" y hay
  // varios — los mostramos para que elija cuál, sin obligarlo a abrir el
  // menú). Si hay cualquiera de los dos, mostramos el grid (Mode A).
  const presel = preselected_ids || [];
  const initialVisible = Array.from(
    new Set([...presel, ...(candidate_ids || [])]),
  );
  const hasGrid = initialVisible.length > 0;

  const [selected, setSelected] = useState<string[]>(() => {
    if (card.status === 'resolved' && card.response?.product_ids) {
      return card.response.product_ids;
    }
    return presel;
  });
  // IDs visibles en el mini-grid de Mode A. Arrancan con los sugeridos del
  // agente, pero cuando el user elige desde el menú fullscreen, este set
  // se reemplaza con su selección. Así el card refleja lo que el user
  // realmente eligió (no la sugerencia original).
  const [visibleIds, setVisibleIds] = useState<string[]>(initialVisible);
  const [fullMenuOpen, setFullMenuOpen] = useState(false);
  // "Subir nuevo producto" abre el alta de producto
  // (ADR-2026-09-25-subir-nuevo-producto-en-la-tarjeta-del-agente). Lo subido vuelve
  // distinto según la variante de la tarjeta: sin grilla se le responde al agente
  // directo —es lo único que el usuario vino a hacer—; con grilla se suma a lo visible
  // y queda tildado, y se confirma con "Confirmar" como el resto.
  const alta = useAltaDeProductoDelChat((nuevos) => {
    if (!hasGrid) {
      onResolve(allowMulti ? nuevos : [nuevos[nuevos.length - 1]]);
      return;
    }
    setVisibleIds((prev) => [...prev, ...nuevos.filter((id) => !prev.includes(id))]);
    setSelected((prev) =>
      allowMulti
        ? [...prev, ...nuevos.filter((id) => !prev.includes(id))]
        : [nuevos[nuevos.length - 1]],
    );
  });

  const toggle = (id: string) => {
    if (!active) return;
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (allowMulti) return [...prev, id];
      return [id];
    });
  };

  // Resumen cuando ya está resolved
  if (card.status === 'resolved') {
    const ids: string[] = card.response?.product_ids || [];
    const products = userProducts.filter((p) => ids.includes(p.id));
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          Producto{products.length > 1 ? 's' : ''}
        </p>
        <div className="flex flex-wrap gap-2">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-2 bg-[#F7F7F9] rounded-full pr-3.5 pl-1 py-1">
              <CachedImg src={p.imageUrl} alt={p.name} className="w-7 h-7 rounded-full object-cover" />
              <span className="text-[13px] font-medium text-brand-black/85">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Mode B: sin preselect — solo 2 CTAs ────────────────────────────
  if (!hasGrid) {
    return (
      <>
        <div className="p-5">
          <p className="text-[15px] font-semibold text-brand-black/90 mb-1">
            {message}
          </p>
          <p className="text-[13px] text-[#3C3C43]/60 mb-4 leading-relaxed">
            Atlas va a usar la foto para armar la imagen final.
          </p>
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              disabled={!active}
              onClick={alta.abrir}
              className="w-full inline-flex items-center justify-center gap-2.5 py-3 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[14.5px] font-semibold shadow-sm transition-colors"
            >
              <Upload className="w-4.5 h-4.5" />
              Subir nuevo producto
            </button>
            <button
              type="button"
              disabled={!active}
              onClick={() => setFullMenuOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2.5 py-3 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/85 text-[14.5px] font-semibold transition-colors"
            >
              <Package className="w-4.5 h-4.5" />
              Elegir de mis productos
            </button>
          </div>
        </div>
        {fullMenuOpen && (
          <ProductFullMenu
            initialSelected={selected}
            allowMulti={allowMulti}
            onCancel={() => setFullMenuOpen(false)}
            onAccept={(ids) => {
              setFullMenuOpen(false);
              // En mode B, aceptar desde el menú resuelve directo el tool —
              // no hay paso de "Confirmar" en el card.
              if (ids.length > 0) onResolve(ids);
            }}
          />
        )}
        {alta.drawer}
      </>
    );
  }

  // ── Mode A: con preselect — mini grid + 2 CTAs + Confirmar sticky ──
  // Renderizamos los productos del set visible — al inicio son los
  // sugeridos, después puede ser lo que el user eligió desde el menú.
  const visibleProducts = userProducts.filter((p) =>
    visibleIds.includes(p.id),
  );

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <p className="text-[15px] font-semibold text-brand-black/90">
          {message}
        </p>
        {allowMulti && (
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            Tocá para confirmar. Podés elegir varios.
          </p>
        )}
      </div>

      {/* Mini-grid de las visibles (3 cols, thumbs chicos). Cada item es la
          imagen en un cuadrado redondeado + el nombre debajo (sin chrome de
          card). Empieza con las sugeridas; si el user usa "Ver todos", se
          reemplaza con lo que elija ahí. */}
      <div className="px-5 pb-3 grid grid-cols-3 gap-2.5">
        {visibleProducts.map((p) => {
          const isSel = selected.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              disabled={!active}
              className="text-left transition-all active:scale-[0.97] disabled:opacity-60"
            >
              <div
                className={`relative aspect-square rounded-xl overflow-hidden bg-neutral-100 ring-2 ${
                  isSel ? 'ring-brand-primary' : 'ring-transparent'
                }`}
              >
                <CachedImg
                  src={p.imageUrl}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {isSel && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center shadow">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                )}
              </div>
              <p className="text-[12px] font-medium text-brand-black/85 truncate mt-1">
                {p.name}
              </p>
            </button>
          );
        })}
      </div>

      {/* CTAs secundarios: Subir imagen + Ver todos */}
      <div className="px-5 pb-4 flex flex-col gap-2">
        <button
          type="button"
          disabled={!active}
          onClick={alta.abrir}
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/80 text-[13.5px] font-medium transition-colors"
        >
          <Upload className="w-4 h-4" />
          Subir nuevo producto
        </button>
        <button
          type="button"
          disabled={!active}
          onClick={() => setFullMenuOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/80 text-[13.5px] font-medium transition-colors"
        >
          <Package className="w-4 h-4" />
          Elegir de mis productos
        </button>
      </div>

      {/* Confirmar — pinned al bottom del card (sticky). Solo aparece si
          al menos una está seleccionada. */}
      {selected.length > 0 && (
        <div className="sticky bottom-0 px-5 pt-3 pb-5 bg-white border-t border-black/[0.04]">
          <button
            type="button"
            onClick={() => onResolve(selected)}
            disabled={!active}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
          >
            Confirmar {selected.length > 1 && `(${selected.length})`}
          </button>
        </div>
      )}

      {fullMenuOpen && (
        <ProductFullMenu
          initialSelected={selected}
          allowMulti={allowMulti}
          onCancel={() => setFullMenuOpen(false)}
          onAccept={(ids) => {
            setSelected(ids);
            // Reemplazamos el set visible con lo que el user eligió en el
            // menú — la sugerencia original deja de mostrarse y aparece
            // lo que realmente eligió.
            setVisibleIds(ids);
            setFullMenuOpen(false);
          }}
        />
      )}
      {alta.drawer}
    </div>
  );
};

// ─── ProductFullMenu ───────────────────────────────────────────────────
// Menú fullscreen con todo el catálogo de productos. Mismo patrón que
// PoseFullMenu: mobile = bottom sheet (portal sobre todo), desktop = side
// panel a la derecha. Toggle multi-select; Aceptar devuelve los IDs al
// card que lo abrió.

export const ProductFullMenu: React.FC<{
  initialSelected: string[];
  allowMulti: boolean;
  onCancel: () => void;
  onAccept: (productIds: string[]) => void;
}> = ({ initialSelected, allowMulti, onCancel, onAccept }) => {
  const rawProducts = useUserProducts();
  const productsLoaded = useUserProductsLoaded();
  // Productos usados en la última generación — llevan badge. Ya no se flotan
  // arriba: para eso está el orden "Últimos utilizados" del toolbar (ver
  // PickProductPanel, mismo criterio).
  const lastUsedIds = useMemo(() => getLastUsedProductIds(), []);
  const lastUsedSet = useMemo(() => new Set(lastUsedIds), [lastUsedIds]);
  // Buscador + orden del catálogo.
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<ProductCatalogSort>(DEFAULT_PRODUCT_SORT);
  const { items: userProducts, noResults } = useMemo(
    () => applyProductCatalogFilter(rawProducts, { query, sort }),
    [rawProducts, query, sort],
  );
  const showToolbar = rawProducts.length >= MIN_PRODUCTS_FOR_TOOLBAR;
  // Mientras el catálogo carga (sin cache aún) mostramos skeleton en vez de
  // un grid vacío.
  const loadingProducts = !productsLoaded && rawProducts.length === 0;
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [animated, setAnimated] = useState(false);

  // ── "Subir nuevo producto" adentro del picker ──
  // El alta y la detección de lo recién subido viven en `useAltaDeProductoDelChat`. Lo
  // nuevo queda SELECCIONADO, que es para lo que el usuario lo subió desde acá.
  const alta = useAltaDeProductoDelChat((nuevos) =>
    setSelected((prev) =>
      allowMulti ? [...prev, ...nuevos.filter((id) => !prev.includes(id))] : [nuevos[nuevos.length - 1]],
    ),
  );

  useEffect(() => {
    requestAnimationFrame(() => setAnimated(true));
  }, []);

  const close = (cb: () => void) => {
    setAnimated(false);
    setTimeout(cb, 280);
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (allowMulti) return [...prev, id];
      return [id];
    });
  };

  const productGrid = loadingProducts ? (
    <ProductGridSkeleton
      count={9}
      gridClassName="grid grid-cols-2 sm:grid-cols-3 gap-3"
      tileClassName="rounded-[13px]"
      withLabel
    />
  ) : noResults ? (
    <ProductCatalogNoResults query={query} onClear={() => setQuery('')} />
  ) : (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {/* Primera tarjeta: agregar un producto nuevo. La MISMA card "Agregar modelo" de
          Mi marca (`AddOptionCard`: borde fino, círculo gris con el +, rótulo gris), en
          acción directa (sin popover). Como hija directa de la grilla se estira al alto
          de la fila, así mide igual que las de producto (foto cuadrada + pie)
          (ADR-2026-09-19-el-picker-de-productos-deja-subir-uno-nuevo). Sin búsqueda
          activa nada más: con un texto en el buscador la grilla es un resultado, y una
          tarjeta fija ahí se leería como un producto que matcheó. */}
      {!query.trim() && (
        <AddOptionCard
          key="__subir-producto__"
          label="Agregar producto"
          rounded="rounded-[13px]"
          className="w-full min-h-[180px]"
          options={[]}
          onClick={alta.abrir}
        />
      )}
      {userProducts.map((p) => {
        const isSel = selected.includes(p.id);
        const isLastUsed = lastUsedSet.has(p.id);
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => toggle(p.id)}
            className={`text-left rounded-[13px] overflow-hidden bg-white transition-all relative active:scale-[0.98] ${
              isSel
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-black/[0.06]'
            }`}
          >
            <div className="relative aspect-square bg-neutral-100">
              <CachedImg
                src={p.imageUrl}
                alt={p.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {isLastUsed && (
                <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-brand-black text-white text-[10px] font-semibold shadow">
                  Último utilizado
                </span>
              )}
              {isSel && (
                <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow-sm">
                  <Check className="w-3.5 h-3.5" />
                </span>
              )}
            </div>
            <p className="text-[13px] text-brand-black/90 px-3 py-2.5 font-medium">
              {p.name}
            </p>
          </button>
        );
      })}
    </div>
  );

  // Toolbar (buscador + orden) + grilla. Se renderiza en los dos viewports; el
  // `padClass` compensa el padding horizontal del contenedor scrolleable de
  // cada uno (para que el fondo blanco del sticky llegue a los bordes) y trae
  // el padding superior, que NO puede vivir en el contenedor scrolleable. Si no
  // hay toolbar, ese padding se lo queda la grilla.
  const renderCatalog = (padClass: string, topClass: string, inputId: string) => {
    const withToolbar = showToolbar && !loadingProducts;
    return (
      <>
        {withToolbar && (
          <ProductCatalogToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            className={`${padClass} ${topClass}`}
            inputId={inputId}
          />
        )}
        {withToolbar ? productGrid : <div className={topClass}>{productGrid}</div>}
      </>
    );
  };

  // ── Mobile bottom sheet ───────────────────────────────────────────
  const mobile = (
    <>
      <div
        className={`md:hidden fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[211] bg-white rounded-t-[13px] flex flex-col transition-transform duration-300 ease-out ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={() => close(onCancel)}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Mis productos
          </h3>
          <button
            type="button"
            onClick={() => {
              if (selected.length === 0) return;
              close(() => onAccept(selected));
            }}
            disabled={selected.length === 0}
            className={`text-[15px] font-semibold w-20 text-right transition-opacity ${
              selected.length > 0
                ? 'text-brand-primary active:opacity-50'
                : 'text-[#3C3C43]/20 cursor-not-allowed'
            }`}
          >
            Aceptar
          </button>
        </div>
        {/* Sin `padding-top` en el scroll: se lo lleva el hint / el toolbar
            sticky, sino la barra se ancla por debajo del padding y quedan las
            cards pasando por arriba. */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {allowMulti && (
            <p className="text-[12.5px] text-[#3C3C43]/55 pt-2 mb-3">
              Podés elegir uno o varios.
            </p>
          )}
          {renderCatalog('-mx-4 px-4', 'pt-3', 'v2-product-full-menu-search-mobile')}
        </div>
      </div>
    </>
  );

  // ── Desktop side panel ────────────────────────────────────────────
  const desktop = (
    <>
      <div
        className={`hidden md:block fixed inset-0 z-[210] bg-black/20 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`hidden md:flex fixed top-4 right-4 bottom-4 w-[560px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[211] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Mis productos
          </h3>
          <button
            type="button"
            onClick={() => close(onCancel)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        {/* Sin `padding-top` en el scroll — ver el comentario del sheet mobile. */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {allowMulti && (
            <p className="text-[13px] text-[#3C3C43]/55 pt-4 mb-3">
              Podés elegir uno o varios.
            </p>
          )}
          {renderCatalog('-mx-5 px-5', 'pt-4', 'v2-product-full-menu-search-desktop')}
        </div>
        <div className="px-5 py-3 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={() => close(onCancel)}
            className="text-[14px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              if (selected.length === 0) return;
              close(() => onAccept(selected));
            }}
            disabled={selected.length === 0}
            className={`text-[14px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
              selected.length > 0
                ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
            }`}
          >
            Aceptar {selected.length > 1 && `(${selected.length})`}
          </button>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(
    <>
      {mobile}
      {desktop}
      {/* Se monta DESPUÉS del picker en el mismo portal: comparten z-index (210/211) y el
          orden en el body es lo que lo deja encima. Al terminar, `refreshProducts`
          actualiza el contexto y el hook selecciona lo nuevo. */}
      {alta.drawer}
    </>,
    document.body,
  );
};

// ─── ModelSubFlowSheet ─────────────────────────────────────────────────
// Sheet (mobile bottom / desktop side panel) que hostea los flujos REALES
// de crear / subir un modelo (CreateView / UploadFlow de ModelPicker) cuando
// el user los abre desde la card de modelo del chat. Al terminar (Utilizar /
// Crear modelo) devuelve el ModelV2 creado y se resuelve el tool pick_model.

const ModelSubFlowSheet: React.FC<{
  mode: 'create' | 'upload';
  onResolved: (model: ModelV2) => void;
  onClose: () => void;
}> = ({ mode, onResolved, onClose }) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const { userId, companyId, refreshModels } = useAgentEnv();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    requestAnimationFrame(() => setAnimated(true));
  }, []);
  const close = (cb: () => void) => {
    setAnimated(false);
    setTimeout(cb, 280);
  };
  const title =
    mode === 'create' ? `Creá tu ${palabra.singular}` : `Cargá tu propio ${palabra.singular}`;

  const content =
    mode === 'create' ? (
      <CreateView
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        onBack={() => close(onClose)}
        onConfirmModel={(m) => close(() => onResolved(m))}
      />
    ) : (
      <UploadFlow
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        onUtilizar={(m) => close(() => onResolved(m))}
      />
    );

  return ReactDOM.createPortal(
    <>
      {/* Mobile bottom sheet */}
      <div
        className={`md:hidden fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onClose)}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[211] bg-white rounded-t-[13px] flex flex-col transition-transform duration-300 ease-out ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-4 pb-2 shrink-0">
          <button
            type="button"
            onClick={() => close(onClose)}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity inline-flex items-center gap-0.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            {title}
          </h3>
          <span className="w-16" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 pt-2 pb-6">{content}</div>
      </div>

      {/* Desktop side panel */}
      <div
        className={`hidden md:block fixed inset-0 z-[210] bg-black/20 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onClose)}
      />
      <div
        className={`hidden md:flex fixed top-4 right-4 bottom-4 w-[560px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[211] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            {title}
          </h3>
          <button
            type="button"
            onClick={() => close(onClose)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 overflow-y-auto px-5 pt-4 md:pb-0">{content}</div>
      </div>
    </>,
    document.body,
  );
};

// ─── PickModelCard ─────────────────────────────────────────────────────
// Slide horizontal de modelos pre-armados. Primera card (sticky a la izq):
// "Crea tu modelo" como CTA destacada. Resto: modelos del catálogo con
// foto de cuerpo completo. Abajo del slide hay un botón secundario para
// subir un modelo propio (mockup por ahora).

type PickModelResolve =
  | { model_id: string; model_name: string }
  | { auto: true }
  | { create_new: true }
  | { upload_own: true };

const PickModelCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (payload: PickModelResolve) => void;
}> = ({ card, active, onResolve }) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const userModels = useUserModels();
  const { message, suggested_hint } = card.input as {
    message: string;
    suggested_hint?: string;
  };
  const [selected, setSelected] = useState<string | null>(null);
  // Sub-flujo abierto (crear / subir modelo) — hostea CreateView/UploadFlow
  // reales en un sheet. null = cerrado.
  const [subFlow, setSubFlow] = useState<null | 'create' | 'upload'>(null);

  if (card.status === 'resolved') {
    const r = card.response;
    let label = '—';
    let img: string | null = null;
    if (r?.model_id) {
      const m = userModels.find((mm) => String(mm.id) === r.model_id);
      label = m?.nombre || r.model_name || '—';
      img = m?.thumbnailUrl || null;
    } else if (r?.auto) {
      label = 'Automático';
    } else if (r?.create_new) {
      label = `${palabra.titulo} a medida`;
    } else if (r?.upload_own) {
      label = `${palabra.titulo} propio`;
    }
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          {palabra.titulo}
        </p>
        <div className="inline-flex items-center gap-2 bg-[#F7F7F9] rounded-full pr-3.5 pl-1 py-1">
          {img ? (
            <CachedImg src={img} alt={label} className="w-7 h-7 rounded-full object-cover bg-neutral-200" />
          ) : (
            <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
            </span>
          )}
          <span className="text-[13px] font-medium text-brand-black/85">{label}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="px-5 pt-5 pb-3">
        <p className="text-[15px] font-semibold text-brand-black/90">{message}</p>
        {suggested_hint && (
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            Sugerido: {suggested_hint}
          </p>
        )}
      </div>

      {/* Slide horizontal de modelos — mismas cards que el menú real de
          modelos (9:16, más grandes, name overlay + check). edge-to-edge a
          la izquierda con padding. */}
      <div
        className="px-5 flex gap-2.5 overflow-x-auto pb-3 scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Automático — card animada (mismo look que el menú real). */}
        <button
          type="button"
          disabled={!active}
          onClick={() => active && setSelected('__auto__')}
          className={`shrink-0 w-[140px] aspect-[9/16] rounded-2xl overflow-hidden relative flex flex-col items-center justify-center gap-2.5 text-white shadow-sm transition-transform active:scale-[0.97] bg-brand-primary disabled:opacity-60 ${
            selected === '__auto__' ? 'ring-2 ring-brand-primary ring-offset-2' : ''
          }`}
        >
          <AnimatedBlurBg
            pauseOnDesktopUnselected
            selected={selected === '__auto__'}
          />
          <Sparkles className="relative z-10 w-7 h-7 drop-shadow" />
          <span className="relative z-10 text-[13.5px] font-semibold leading-tight px-2 text-center drop-shadow">
            Automático
          </span>
        </button>

        {userModels.map((m) => {
          const id = String(m.id);
          const isSel = selected === id;
          const isGlobal = m.idCompany === null;
          return (
            <button
              key={id}
              type="button"
              disabled={!active}
              onClick={() => active && setSelected(id)}
              className={`shrink-0 w-[140px] aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                isSel ? 'border-brand-primary' : 'border-neutral-200'
              } transition-all relative active:scale-[0.97]`}
            >
              {m.thumbUrl && (
                <CachedImg
                  src={m.thumbUrl}
                  alt={m.nombre}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden';
                  }}
                />
              )}
              {isGlobal && (
                <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-black/55 backdrop-blur-sm text-white text-[9.5px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md">
                  <Sparkles className="w-2.5 h-2.5" />
                  Atlas
                </span>
              )}
              {isSel && (
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/65 via-black/15 to-transparent">
                <p className="text-left text-white text-[13px] font-semibold leading-tight drop-shadow truncate">
                  {m.nombre}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Crear / Cargar modelo — 2 botones full-width debajo del slide,
          mismo estilo que el menú real de modelos (icon-circle + chevron). */}
      <div className="px-5 pt-2 pb-3 flex flex-col gap-2.5">
        <button
          type="button"
          disabled={!active}
          onClick={() => active && setSubFlow('create')}
          className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
        >
          <span className="inline-flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
            </span>
            Creá tu {palabra.singular}
          </span>
          <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
        </button>
        <button
          type="button"
          disabled={!active}
          onClick={() => active && setSubFlow('upload')}
          className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
        >
          <span className="inline-flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <UserPlus className="w-3.5 h-3.5 text-brand-primary" />
            </span>
            Cargá tu propio {palabra.singular}
          </span>
          <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
        </button>
      </div>

      {subFlow && (
        <ModelSubFlowSheet
          mode={subFlow}
          onClose={() => setSubFlow(null)}
          onResolved={(m) => {
            setSubFlow(null);
            onResolve({ model_id: String(m.id), model_name: m.nombre });
          }}
        />
      )}

      <div className="px-5 pb-5 pt-1">
        <button
          type="button"
          onClick={() => {
            if (!selected) return;
            if (selected === '__auto__') {
              onResolve({ auto: true });
              return;
            }
            const m = userModels.find((x) => String(x.id) === selected)!;
            onResolve({ model_id: String(m.id), model_name: m.nombre });
          }}
          disabled={!active || !selected}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          Confirmar
        </button>
      </div>
    </div>
  );
};

// ─── PickPoseCard ──────────────────────────────────────────────────────
// Compacto en el slot bottom: muestra hasta 6 poses (sugeridas + relleno
// hasta completar la grilla). Botón "Ver todas" abre el menú fullscreen
// con tabs por categoría — mismo patrón que el pose menu de
// PruebaNuevaInterfaz para que la UX sea consistente.

const COMPACT_POSE_COUNT = 3;

const PickPoseCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (poseIds: string[], poseNames: string[]) => void;
}> = ({ card, active, onResolve }) => {
  const { message, suggested_pose_ids } = card.input as {
    message: string;
    suggested_pose_ids?: string[];
  };
  const { poses, loading } = usePoses();
  // Multi-select: el user puede elegir varias poses (se genera una imagen
  // por cada una, igual que el modo modelo).
  const [selected, setSelected] = useState<string[]>([]);
  const [fullMenuOpen, setFullMenuOpen] = useState(false);

  const toggle = (id: string) => {
    if (!active) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Compact preview: sugeridas primero, después relleno hasta
  // COMPACT_POSE_COUNT. Las seleccionadas que no estén en el set base se
  // intercalan al principio para que siempre se vean acá.
  const compactPoses = useMemo(() => {
    if (poses.length === 0) return [];
    const sugSet = new Set(suggested_pose_ids || []);
    const sug = poses.filter((p) => sugSet.has(p.id));
    const rest = poses.filter((p) => !sugSet.has(p.id));
    let base = [...sug, ...rest].slice(0, COMPACT_POSE_COUNT);
    const extra = selected
      .filter((id) => !base.some((p) => p.id === id))
      .map((id) => poses.find((p) => p.id === id))
      .filter((p): p is PoseV2 => !!p);
    if (extra.length > 0) {
      base = [...extra, ...base].slice(0, COMPACT_POSE_COUNT);
    }
    return base;
  }, [poses, suggested_pose_ids, selected]);

  if (card.status === 'resolved') {
    const ids: string[] = card.response?.pose_ids || [];
    const sel = poses.filter((p) => ids.includes(p.id));
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          Pose{sel.length > 1 ? 's' : ''}
        </p>
        <div className="flex flex-wrap gap-2">
          {sel.map((pose) => (
            <div
              key={pose.id}
              className="inline-flex items-center gap-2 bg-[#F7F7F9] rounded-full pr-3.5 pl-1 py-1"
            >
              {pose.imageUrl && (
                <img
                  src={pose.imageUrl}
                  alt={pose.nombre}
                  className="w-7 h-7 rounded-full object-cover bg-neutral-200"
                />
              )}
              <span className="text-[13px] font-medium text-brand-black/85">
                {pose.nombre}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const sugSet = new Set(suggested_pose_ids || []);

  return (
    <div className="flex flex-col">
      <div className="px-5 pt-5 pb-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-brand-black/90">{message}</p>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            {(suggested_pose_ids?.length || 0) > 0
              ? 'Recomendadas para tu producto. Elegí una o varias — se genera una imagen por cada pose.'
              : 'Elegí una o varias — se genera una imagen por cada pose.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFullMenuOpen(true)}
          disabled={!active}
          className="inline-flex items-center gap-0.5 shrink-0 text-brand-primary text-[13px] font-medium hover:opacity-75 transition-opacity disabled:opacity-40"
        >
          Ver todas
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-5 pb-3">
        {loading ? (
          <div className="grid grid-cols-3 gap-2.5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2.5">
            {compactPoses.map((p) => {
              const isSel = selected.includes(p.id);
              const isSug = sugSet.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id)}
                  disabled={!active}
                  className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 border-2 ${
                    isSel
                      ? 'border-brand-primary ring-2 ring-brand-primary/20'
                      : 'border-transparent'
                  } transition-all active:scale-[0.97]`}
                >
                  {p.imageUrl && (
                    <img
                      src={p.imageUrl}
                      alt={p.nombre}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  {isSug && !isSel && (
                    <span className="absolute top-1.5 left-1.5 inline-block bg-brand-primary text-white text-[9.5px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md">
                      Sugerida
                    </span>
                  )}
                  {isSel && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center shadow">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="px-5 pb-5 pt-2">
        <button
          type="button"
          onClick={() => {
            if (selected.length === 0) return;
            const names = selected.map(
              (id) => poses.find((p) => p.id === id)?.nombre || '',
            );
            onResolve(selected, names);
          }}
          disabled={!active || selected.length === 0}
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          Confirmar {selected.length > 1 && `(${selected.length})`}
        </button>
      </div>

      {/* Menú real de poses (PosePickerSheet) — mismo que el modo modelo /
          plantillas. Multi-select. */}
      <PosePickerSheet
        open={fullMenuOpen}
        poses={poses}
        selectedIds={selected}
        onChange={(ids) => setSelected(ids)}
        onClose={() => setFullMenuOpen(false)}
      />
    </div>
  );
};

// ─── PickFormatCard ────────────────────────────────────────────────────

const PickFormatCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (format: string) => void;
}> = ({ card, active, onResolve }) => {
  const { message, suggested_format } = card.input as {
    message: string;
    suggested_format?: string;
  };
  const formatOption = CONFIG_OPTIONS_V2.find((o) => o.id === 'formato')!;
  const [selected, setSelected] = useState<string>(
    suggested_format || formatOption.values[0],
  );

  if (card.status === 'resolved') {
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          Formato
        </p>
        <span className="inline-block bg-[#F7F7F9] rounded-full px-3.5 py-1 text-[13px] font-medium text-brand-black/85">
          {card.response?.format || '—'}
        </span>
      </div>
    );
  }

  return (
    <div className="p-5">
      <p className="text-[15px] font-semibold text-brand-black/90 mb-3">{message}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {formatOption.values.map((v) => (
          <button
            key={v}
            type="button"
            disabled={!active}
            onClick={() => setSelected(v)}
            className={`px-4 py-2 rounded-full border text-[13.5px] font-medium transition-all ${
              selected === v
                ? 'bg-brand-primary border-brand-primary text-white'
                : 'bg-white border-neutral-200 text-brand-black/80 hover:border-brand-primary/40'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onResolve(selected)}
        disabled={!active}
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[14.5px] font-semibold shadow-sm transition-colors"
      >
        Confirmar
      </button>
    </div>
  );
};

// ─── PickBackgroundCard ────────────────────────────────────────────────
// Picker de fondo para el flujo con modelo. Quick-options (Automático /
// Blanco) inline + un botón que abre el FondoPickerSheet completo
// (biblioteca / guardados / describir / subir). Resuelve con la FondoSelection.
const PickBackgroundCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (fondo: FondoSelection) => void;
}> = ({ card, active, onResolve }) => {
  const { message, suggested } = card.input as {
    message: string;
    suggested?: { mode?: string; id?: number | string; text?: string };
  };
  const { companyId } = useAgentEnv();
  const { biblioteca, guardados, reload } = useFondos();
  const [sheetOpen, setSheetOpen] = useState(false);
  const suggestedSel = useMemo(
    () => agentBgToFondoSelection(suggested, biblioteca, guardados),
    [suggested, biblioteca, guardados],
  );

  if (card.status === 'resolved') {
    const sel: FondoSelection | null = card.response?.fondo ?? null;
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          Fondo
        </p>
        <span className="inline-block bg-[#F7F7F9] rounded-full px-3.5 py-1 text-[13px] font-medium text-brand-black/85 max-w-full truncate">
          {sel
            ? sel.mode === 'descripcion'
              ? sel.text || 'Descripción'
              : fondoSelectionLabel(sel)
            : 'Automático'}
        </span>
      </div>
    );
  }

  return (
    <div className="p-5">
      <p className="text-[15px] font-semibold text-brand-black/90 mb-3">
        {message}
      </p>
      {suggestedSel && (
        <button
          type="button"
          disabled={!active}
          onClick={() => onResolve(suggestedSel)}
          className="w-full mb-3 inline-flex items-center justify-between gap-2 px-4 py-3 rounded-2xl bg-brand-primary/[0.06] border border-brand-primary/20 text-left disabled:opacity-50 hover:bg-brand-primary/[0.1] transition-colors"
        >
          <span className="text-[13.5px] font-medium text-brand-black/85 truncate">
            Sugerido:{' '}
            {suggestedSel.mode === 'descripcion'
              ? suggestedSel.text || 'Descripción'
              : fondoSelectionLabel(suggestedSel)}
          </span>
          <Check className="w-4 h-4 text-brand-primary shrink-0" />
        </button>
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          disabled={!active}
          onClick={() => onResolve({ mode: 'auto' })}
          className="px-4 py-2 rounded-full border border-neutral-200 bg-white text-[13.5px] font-medium text-brand-black/80 hover:border-brand-primary/40 disabled:opacity-50 transition-all"
        >
          Automático
        </button>
        <button
          type="button"
          disabled={!active}
          onClick={() => onResolve({ mode: 'blanco' })}
          className="px-4 py-2 rounded-full border border-neutral-200 bg-white text-[13.5px] font-medium text-brand-black/80 hover:border-brand-primary/40 disabled:opacity-50 transition-all"
        >
          Fondo blanco
        </button>
      </div>
      <button
        type="button"
        disabled={!active}
        onClick={() => setSheetOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[14.5px] font-semibold shadow-sm transition-colors"
      >
        Elegir o describir fondo
      </button>
      <FondoPickerSheet
        open={sheetOpen}
        selection={suggestedSel}
        onChange={(sel) => {
          setSheetOpen(false);
          onResolve(sel);
        }}
        onClose={() => setSheetOpen(false)}
        biblioteca={biblioteca}
        guardados={guardados}
        companyId={companyId}
        onFondoSaved={reload}
      />
    </div>
  );
};

// (La vista de carga de las cards vive en `GenerandoEnCard.tsx`: es la misma animación
//  que la columna de resultados del modo Modelo. La Lottie que había acá se retiró —
//  ADR-2026-09-09-las-cards-del-chat-generan-como-el-panel.)

// ─── GenerationResultView ──────────────────────────────────────────────
// Render de la card una vez generada: thumbnails de las imágenes + botón
// "Abrir resultados" (lleva al visor completo). Deriva todo de los ids
// guardados en el response (sobrevive el cambio de instancia cuando la card
// pasa de activa a resuelta) y fetchea las URLs firmadas con preload.

const GenerationResultView: React.FC<{
  imageIds: number[];
  companyId: string;
  /** Engancha la imagen al composer ("Agregar"): el mismo chip `gen-<id>` que arma la
   *  biblioteca, así no se duplica si ya estaba. */
  onAgregar?: (a: ChatAttachment) => void;
}> = ({ imageIds, companyId, onAgregar }) => {
  const navigate = useNavigate();
  const [thumbs, setThumbs] = useState<PreloadedImage[]>([]);
  useEffect(() => {
    let alive = true;
    preloadGeneratedImages(imageIds, companyId)
      .then((imgs) => {
        if (alive) setThumbs(imgs);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [imageIds, companyId]);

  // Con el lienzo a la vista, la imagen se abre en SU visor (la vista expandida) en vez
  // de ir a la galería (ADR-2026-09-18-la-foto-del-chat-se-abre-en-el-visor-del-lienzo).
  const visorDelLienzo = useVisorDelLienzo();
  const open = (imageId: string) => {
    if (visorDelLienzo) {
      visorDelLienzo(imageId);
      return;
    }
    navigate('/redes-sociales/imagenes-generadas', {
      state: { imageIds, companyId, preloadedImages: thumbs },
    });
  };

  const cells =
    thumbs.length > 0
      ? thumbs
      : imageIds.map((id) => ({ id, signedUrl: '' } as PreloadedImage));

  // SOLO la imagen: sin el rótulo "Listo", sin la línea "Generamos tu imagen." y
  // sin el botón "Abrir resultados" — ADR-2026-09-08-el-resultado-es-solo-la-imagen.
  //
  // El botón se fue pero la capacidad no: la imagen ES el botón, sigue abriendo
  // el visor al clickearla. Se sacó el cartel, no la acción.
  //
  // Las imágenes ya NO van en una grilla de tres columnas recortadas a cuadrado:
  // van a su proporción real, acotadas por ALTO. Con `w-auto` + `max-h`, el ancho
  // sale de la imagen, y es eso lo que deja que la card blanca se achique a su
  // medida (el `fit` que pide `ToolCardRenderer`) en vez de ocupar la columna.
  //
  // El ALTO baja cuando son varias, con el MISMO presupuesto que la espera
  // (`ChatGeneratingView`): con 46dvh cada una, dos imágenes ya no entraban a lo
  // ancho y el `flex-wrap` las apilaba de a una por fila — se veían en columna
  // aunque el contenedor fuera una fila. Acotándolas, la tanda se lee de un
  // vistazo, una al lado de la otra, y el resultado ocupa el mismo lugar que la
  // espera que acaba de estar ahí.
  const altoMax =
    cells.length === 1
      ? 'min(46dvh, 420px)'
      : cells.length === 2
        ? 'min(34dvh, 300px)'
        : 'min(26dvh, 220px)';

  return (
    <div className="p-2 flex flex-wrap items-start gap-2">
      {cells.map((t) => (
        // `relative` + dos botones HERMANOS: el de "Editar" no puede ir ADENTRO
        // del que abre el visor (un <button> dentro de otro es HTML inválido y
        // el click del de adentro burbujea al de afuera).
        <div
          key={t.id}
          className="relative rounded-xl overflow-hidden bg-neutral-100"
        >
          <button
            type="button"
            onClick={() => open(String(t.id))}
            aria-label="Abrir resultados"
            className="block active:scale-[0.99] transition-transform"
          >
            {t.signedUrl ? (
              <CachedImg
                src={t.signedUrl}
                alt="Imagen generada"
                className="block w-auto max-w-full h-auto object-contain"
                style={{ maxHeight: altoMax }}
              />
            ) : (
              // Sin URL todavía: un cuadrado que reserva algo de lugar. Con `h-auto`
              // y sin imagen el botón mediría 0 y la card parpadearía a nada.
              <div className="w-40 h-40 animate-pulse bg-neutral-100" />
            )}
          </button>
          {/* "Agregar" ADENTRO de la imagen, abajo a la izquierda: engancha ESA imagen al
              composer para seguir trabajándola con el agente. Antes era "Editar" (la
              puerta al editor de siempre); Matías lo cambió el 2026-09-16 — el editor
              sigue a mano desde el visor
              (ADR-2026-09-16-la-foto-del-chat-se-agrega-en-vez-de-editarse).

              Siempre visible, no al hover: en touch el hover no existe y un
              atajo que no se ve es un atajo que no existe. Sólo cuando la
              miniatura ya cargó y hay quien reciba el chip: sin imagen, el botón
              flota sobre un placeholder gris. */}
          {t.signedUrl && onAgregar && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAgregar({
                  kind: 'image',
                  id: `gen-${t.id}`,
                  previewUrl: t.signedUrl,
                  storagePath: `${companyId}/full/${t.fileName}`,
                  bucket: 'generadas',
                });
              }}
              className="absolute left-2 bottom-2 inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full bg-white/90 backdrop-blur-md shadow-sm text-[12.5px] font-semibold text-brand-black/85 hover:bg-white transition-colors"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.25} />
              Agregar
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── RecentMediaCard ───────────────────────────────────────────────────
// Galería de las ÚLTIMAS imágenes/videos que el usuario generó (las trae de
// `imagenes_generadas` por su cuenta). La dispara la tool `show_recent_media`.
// Se AUTO-RESUELVE de inmediato (no espera el fetch) para que aparezca en el
// CHAT y no quede colgada en el slot de abajo. Muestra thumbnails 4:5 (mismo
// aspecto que el Historial) — videos con su poster `.png` + badge de play — y
// al tocar navega al MISMO detalle que el Historial (editar-imagen/-video).

type RecentItem = {
  id: string;
  isVideo: boolean;
  thumbUrl: string;
  /** Etiqueta corta (prompt o nombre de producto) — para que el agente pueda
   *  mapear una referencia DESCRIPTIVA ("la del vestido verde") a esta pieza. */
  label: string;
};

// Firma el thumbnail de una generación como lo hace el Historial: bucket
// `generadas`, primero `opt/` (miniatura), fallback `full/`; para videos el
// poster es el mismo nombre con extensión `.png`.
//
// Va reescalada por Storage a 400 px (`TRANSFORM_MINIATURA_PARA_IDENTIFICAR`): esta URL
// es la que el chat le reenvía al modelo en el turno siguiente sólo para que reconozca
// cuál le piden, y a 800 px cada una costaba ~1.000 tokens por turno. Para la tarjeta
// alcanza igual. `refrescarFirmas` sabe renovarla con la misma transformación.
export async function signRecentThumb(
  companyId: string,
  fileName: string,
  isVideo: boolean,
): Promise<string> {
  if (!fileName) return '';
  const thumbName = isVideo
    ? fileName.replace(/\.(mp4|webm|mov)$/i, '.png')
    : fileName;
  for (const path of [
    `${companyId}/opt/${thumbName}`,
    `${companyId}/full/${thumbName}`,
  ]) {
    try {
      const { data, error } = await supabase.storage
        .from('generadas')
        .createSignedUrl(path, 3600, { transform: TRANSFORM_MINIATURA_PARA_IDENTIFICAR });
      if (!error && data?.signedUrl) return data.signedUrl;
    } catch {
      /* probamos el siguiente path */
    }
  }
  return '';
}

const RecentMediaCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const navigate = useNavigate();
  const { companyId, brandId, variasMarcas } = useAgentEnv();
  // Las creaciones son las de la MARCA ACTIVA — ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
  // Esta galería y `PickCreacionCard` eran las dos únicas superficies de creaciones que
  // seguían listando toda la empresa: el agente le mostraba al usuario lo que había
  // generado para OTRA marca. Mismo criterio que la home, el historial y la columna del
  // modo: la marca o sin marca (`orDeMarca`), y sin filtrar en cuentas de una sola marca.
  const marcaDelFiltro = marcaParaFiltrar(brandId, variasMarcas === true);
  const limit = Math.min(
    12,
    Math.max(1, Number((card.input as any)?.limit) || 6),
  );
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<RecentItem[]>([]);
  const resolvedRef = useRef(false);

  // Resolver YA (sin esperar el fetch) → la card se resuelve al instante y se
  // renderiza en el CHAT, no en el slot inferior. Los thumbnails cargan inline.
  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { shown: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Depende de companyId: al volver al chat tras navegar, InterfazV2 remonta y
  // companyId arranca en null y carga async → sin esta dependencia el fetch
  // corría una vez con companyId null y la galería quedaba VACÍA para siempre.
  useEffect(() => {
    if (!companyId) return; // esperamos a tener companyId
    let alive = true;
    setLoading(true);
    (async () => {
      const out: RecentItem[] = [];
      try {
        let q = supabase
          .from('imagenes_generadas')
          .select(
            'id, tipo, file_name, prompt, variante_id, variantes:variantes(nombre)',
          )
          .eq('id_company', companyId)
          .eq('active', true);
        if (marcaDelFiltro) q = q.or(orDeMarca(marcaDelFiltro));
        const { data } = await q
          .order('created_at', { ascending: false })
          .limit(limit);
        const rows = (data as any[]) || [];
        const signed = await Promise.all(
          rows.map(async (r) => {
            const isVideo = r.tipo === 'video' || r.tipo === 'video_pro';
            const thumbUrl = await signRecentThumb(
              companyId,
              r.file_name || '',
              isVideo,
            );
            // Etiqueta: el prompt con que se generó, o el nombre del producto.
            const raw = String(r.prompt || r.variantes?.nombre || '').trim();
            const label = raw
              ? raw.slice(0, 90)
              : isVideo
                ? 'video'
                : 'imagen';
            return { id: String(r.id), isVideo, thumbUrl, label };
          }),
        );
        out.push(...signed);
      } catch {
        /* sin datos → estado vacío */
      }
      if (alive) {
        setItems(out);
        setLoading(false);
        // Guardamos QUÉ mostramos (id + tipo, en orden) en la respuesta de la
        // card. El mecanismo [registro:] lo lee en el próximo turno, así el
        // agente puede mapear "la 1ra/2da/última" a un image_id y animarla —
        // sin pedirle el id al usuario. (La card ya se resolvió con {shown};
        // esto solo enriquece la respuesta para el historial.)
        chat.patchToolCard(card.id, {
          response: {
            shown: true,
            media: out.map((it) => ({
              id: it.id,
              isVideo: it.isVideo,
              label: it.label,
              // thumbUrl (firmada 1h): permite que el agente VEA la imagen en
              // el turno siguiente (se adjunta al mensaje del usuario) e
              // identifique la que describa. Videos no llevan (no se pasan).
              thumbUrl: it.isVideo ? undefined : it.thumbUrl,
            })),
          },
        });
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, marcaDelFiltro, limit]);

  // Tap → abre la MISMA vista ampliada (lightbox) del Historial: navega a
  // `/redes-sociales/imagenes` con `expandImageId`, que hace que el Historial
  // abra ese ítem expandido al montar (soporta imágenes y videos).
  const openItem = (it: RecentItem) =>
    navigate('/redes-sociales/imagenes', {
      state: { expandImageId: it.id },
    });

  const Header = (
    <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
      Tus últimas generaciones
    </p>
  );

  if (loading) {
    return (
      <div className="p-4">
        {Header}
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="aspect-[4/5] rounded-xl bg-neutral-100 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-4">
        {Header}
        <p className="text-[14px] text-brand-black/85 leading-snug">
          Todavía no encontré nada generado. Cuando crees una imagen o un video,
          van a aparecer acá.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {Header}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => openItem(it)}
            className="relative aspect-[4/5] rounded-xl overflow-hidden bg-neutral-100 active:scale-[0.97] transition-transform"
          >
            {it.thumbUrl ? (
              <CachedImg
                src={it.thumbUrl}
                alt={it.isVideo ? 'Video generado' : 'Imagen generada'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-neutral-100" />
            )}
            {it.isVideo && (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-8 h-8 rounded-full bg-black/45 flex items-center justify-center">
                  <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                </span>
              </span>
            )}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => navigate('/redes-sociales/imagenes')}
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-neutral-100 hover:bg-neutral-200 text-brand-black/85 text-[14.5px] font-semibold transition-colors"
      >
        Ver todo el historial
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── ConectarTiendaCard ───────────────────────────────────────────────
// El botón para ir a conectar la Tiendanube. La dispara `conectar_tienda`
// cuando la cuenta no la tiene conectada.
//
// Por qué una tarjeta y no una navegación directa: el agente puede LLEVAR al
// usuario a una pantalla, pero hacerlo en la misma respuesta le cambia lo que
// está leyendo a mitad de la frase. El toque lo da la persona cuando terminó de
// leer. Se auto-resuelve al montar para que el agente siga contestando.

const ConectarTiendaCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { mostrado: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] leading-snug text-brand-black/80">
        Para traer tus productos y subir fotos, Atlas necesita estar conectado a tu
        Tiendanube. Se hace una sola vez.
      </p>
      <button
        type="button"
        onClick={() => navigate('/mi-marca')}
        className="inline-flex w-fit items-center gap-2 rounded-xl bg-brand-primary px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
      >
        Ir a conectar Tiendanube
      </button>
    </div>
  );
};

// ─── ConectarInstagramCard ─────────────────────────────────────────────
// El botón para ir a conectar el Instagram de la marca. Mismo molde y mismo porqué
// que `ConectarTiendaCard`: no navega sola, y se auto-resuelve al montar para que el
// agente siga (ADR-2026-09-24-instagram-boton-en-mi-marca).

const ConectarInstagramCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { mostrado: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Una fila, como la de Instagram en Mi marca: el ícono, qué es y el botón. El agente
  // ya explica en su texto qué va a poder ver; la tarjeta no lo repite.
  return (
    <div className="flex items-center justify-between gap-8 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <Instagram className="h-[18px] w-[18px] shrink-0 text-[#c13584]" />
        <span className="text-[14px] font-medium text-brand-black/80">Instagram</span>
      </div>
      <button
        type="button"
        onClick={() => navigate('/mi-marca')}
        className="shrink-0 inline-flex h-8 items-center rounded-md bg-brand-primary px-3 text-[13px] font-medium text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
      >
        Ir a conectar
      </button>
    </div>
  );
};

// ─── ConectarMercadoLibreCard ──────────────────────────────────────────
// Lo mismo para Mercado Libre (ADR-2026-09-28-mercadolibre-en-el-chat). El botón va a Mi
// marca con el modal de conectar ya abierto (deep-link `?conectar=mercadolibre`).

const ConectarMercadoLibreCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { mostrado: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center justify-between gap-8 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <MercadoLibreIcon className="h-[18px] w-[18px]" />
        <span className="text-[14px] font-medium text-brand-black/80">Mercado Libre</span>
      </div>
      <button
        type="button"
        onClick={() => navigate('/mi-marca?conectar=mercadolibre')}
        className="shrink-0 inline-flex h-8 items-center rounded-md bg-brand-primary px-3 text-[13px] font-medium text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
      >
        Ir a conectar
      </button>
    </div>
  );
};

// ─── ConectarAnunciosCard ──────────────────────────────────────────────
// Lo mismo para los anuncios de Meta: una fila como la de Mi marca y el botón para ir
// (ADR-2026-09-24-anuncios-de-meta-en-el-chat).

const ConectarAnunciosCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { mostrado: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center justify-between gap-8 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <Megaphone className="h-[18px] w-[18px] shrink-0 text-[#0866FF]" />
        <span className="text-[14px] font-medium text-brand-black/80">Anuncios de Meta</span>
      </div>
      <button
        type="button"
        onClick={() => navigate('/mi-marca')}
        className="shrink-0 inline-flex h-8 items-center rounded-md bg-brand-primary px-3 text-[13px] font-medium text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
      >
        Ir a conectar
      </button>
    </div>
  );
};

// ─── ContactSupportCard ────────────────────────────────────────────────
// Tarjeta para contactar al soporte por WhatsApp con un mensaje pre-armado.
// La dispara la tool `contact_support(problema)`: el saludo lo arma el
// cliente (nombre + marca del env, NO lo controla el agente) y el agente
// solo aporta la descripción del problema. Mobile → botón WhatsApp; desktop
// → QR. Se AUTO-RESUELVE al montar (`{shown:true}`) para que el agente siga.

const SUPPORT_WHATSAPP_NUMBER = '5491122635392';

const ContactSupportCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const { shortName, companyName } = useAgentEnv();
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!resolvedRef.current && card.status === 'pending') {
      resolvedRef.current = true;
      chat.resolveToolCard(card.id, { shown: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const problema = String((card.input as any)?.problema || '').trim();
  // Prefijo FIJO (misma fórmula que el resto de la app) — el agente no lo
  // controla, solo aporta `problema`.
  const saludo = `Buenas, soy ${shortName || ''} de ${companyName || ''}.`;
  const message = problema ? `${saludo} ${problema}` : saludo;
  const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message,
  )}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
    whatsappUrl,
  )}`;

  return (
    <div className="p-4">
      <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
        Contactar soporte
      </p>
      <p className="text-[14px] text-brand-black/85 leading-snug mb-3">
        Te dejé el mensaje listo para enviarle al equipo de soporte:
      </p>
      <div className="rounded-xl bg-[#F7F7F9] px-3.5 py-3 mb-4 text-[13.5px] text-brand-black/80 leading-snug whitespace-pre-wrap">
        {message}
      </div>

      {/* Mobile: botón WhatsApp */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="sm:hidden w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
      >
        Contactar por WhatsApp
      </a>

      {/* Desktop: QR para escanear */}
      <div className="hidden sm:flex flex-col items-center">
        <img
          src={qrUrl}
          alt="QR para contactar soporte por WhatsApp"
          className="w-40 h-40 rounded-xl"
        />
        <p className="text-[12.5px] text-[#3C3C43]/55 mt-2 text-center leading-snug">
          Escaneá el código con la cámara del celular para abrir el chat de
          soporte con el mensaje cargado.
        </p>
      </div>
    </div>
  );
};

// ─── ConfirmResearchCard ───────────────────────────────────────────────
// Confirmación antes de habilitar el scraping de datos externos (Apify), que
// puede consumir varios créditos. Aceptar → el agente puede scrapear el resto
// del chat; Cancelar → no se habilita. La dispara la tool `activar_investigacion`.

const ConfirmResearchCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (accepted: boolean) => void;
  /** El botón blanco: acepta y deja la investigación en directo. `undefined` ⇒ sin botón
   *  (ADR-2026-09-13-generar-sin-preguntarme-en-la-card). */
  noPreguntarMas?: UseV2ChatReturn['noPreguntarMas'];
}> = ({ card, active, onResolve, noPreguntarMas }) => {
  const resolved = card.status === 'resolved';
  const cancelled = card.status === 'cancelled';

  if (resolved || cancelled) {
    return (
      <div className="p-4 flex items-center gap-2.5">
        <span
          className={`inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${
            resolved
              ? 'bg-brand-primary/10 text-brand-primary'
              : 'bg-black/[0.05] text-brand-black/50'
          }`}
        >
          {resolved ? <Check size={16} /> : <X size={16} />}
        </span>
        <p className="text-[13.5px] font-medium text-brand-black/70">
          {resolved ? 'Investigación habilitada' : 'Investigación cancelada'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary shrink-0">
          <Search size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-brand-black">
            Investigar datos externos
          </p>
          <p className="text-[13.5px] text-brand-black/60 mt-0.5 leading-snug">
            Esta acción analiza datos reales de plataformas externas (Instagram,
            Facebook, tiendas). Puede tardar un momento y consumir varios créditos.
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={!active}
          onClick={() => onResolve(false)}
          className="flex-1 py-2.5 px-5 rounded-xl bg-white border border-neutral-200 hover:border-brand-primary/40 disabled:opacity-40 text-brand-black/80 text-[14px] font-semibold transition-colors"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!active}
          onClick={() => onResolve(true)}
          className="flex-1 py-2.5 px-5 rounded-xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[14px] font-semibold shadow-sm transition-colors"
        >
          Aceptar
        </button>
      </div>
      <BotonGenerarSinPreguntar
        tipo="investigacion"
        noPreguntarMas={noPreguntarMas}
        onAccion={() => onResolve(true)}
        disabled={!active}
      />
    </div>
  );
};

// ─── Recuperación de generaciones del agente ───────────────────────────
// Antes de disparar una generación, la card guarda `pending_generation`
// (execution_id + company + conteo) EN SU INPUT vía patchToolCard — como el
// chat se persiste (sessionStorage + DB), el pendiente sobrevive a recargas
// e incluso a abrir el chat desde otro dispositivo. Si la pestaña muere a
// mitad de la generación, la card queda 'cancelled' (los resolvers no
// sobreviven) pero con el pendiente en el input: al montar de nuevo, este
// hook retoma el poll por execution_id y, si la tanda salió, resuelve la
// card DIRECTO con patchToolCard (el resolver del agente ya no existe —
// el agente no se entera, pero el usuario ve su resultado sin re-pagar).

/** Shape del pendiente persistido en el input de la card. */
interface PendingGenerationInput {
  execution_id?: string;
  company_id?: string;
  expected_count?: number;
  started_at?: number;
}

// Ventana para retomar (más viejo = no reanudar; si salió, está en el historial).
// Imagen alineada al recovery manual; video más larga porque la generación en sí tarda
// varios minutos.
const PENDING_IMAGE_MAX_AGE_MS = 10 * 60 * 1000;
const PENDING_VIDEO_MAX_AGE_MS = 20 * 60 * 1000;

/** Reanuda una generación de IMAGEN pendiente al montar la card (si hay).
 *  `setBusy` muestra el estado "Generando…" mientras el poll corre. */
const useResumeImageGeneration = (
  card: V2ToolCard,
  chat: UseV2ChatReturn,
  setBusy: (v: boolean) => void,
) => {
  const firedRef = useRef(false);
  useEffect(() => {
    const pending = (card.input as any)
      ?.pending_generation as PendingGenerationInput | undefined;
    if (
      firedRef.current ||
      card.status === 'resolved' ||
      !pending?.execution_id ||
      !pending?.company_id
    )
      return;
    if (Date.now() - (pending.started_at || 0) > PENDING_IMAGE_MAX_AGE_MS)
      return;
    firedRef.current = true;
    setBusy(true);
    (async () => {
      try {
        const { pollGeneratedByExecutionId } = await import(
          './pollGeneratedByExecutionId'
        );
        const rows = await pollGeneratedByExecutionId({
          executionId: pending.execution_id!,
          companyId: pending.company_id!,
          expectedCount: pending.expected_count,
        });
        const ok = rows.filter((r) => !r.has_error);
        if (ok.length > 0) {
          // `resolveToolCard` y NO `patchToolCard`: patch cambia el estado
          // visual pero deja la promesa de `pushToolCard` colgada para
          // siempre. El modo directo la espera para navegar a las generadas,
          // así que con patch se quedaba en la pantalla de carga. Si ya no
          // hay resolver (sesión restaurada), resolveToolCard cae igual al
          // update directo.
          chat.resolveToolCard(card.id, {
            confirmed: true,
            image_ids: ok.map((r) => r.id),
            company_id: pending.company_id,
          });
        }
      } catch {
        /* sin recuperación posible → la card queda como estaba */
      }
      setBusy(false);
    })();
    // Solo al montar: el pendiente viene del input persistido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// ─── ConfirmGenerateCard ───────────────────────────────────────────────

const ConfirmGenerateCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
  messages: V2ChatMessage[];
  /** Avisa hacia arriba cuándo esta card está mostrando la espera. El recuadro
   *  blanco lo necesita para achicarse a la caja de carga, y "está generando" es
   *  `useState` de acá adentro: no se puede derivar de `card`
   *  (ADR-2026-09-08-el-recuadro-de-la-espera-se-achica-a-las-cajas). */
  onCargandoChange?: (cargando: boolean) => void;
}> = ({ card, active, chat, messages, onCargandoChange }) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  const { biblioteca: fondoBiblioteca, guardados: fondoGuardados, reload: reloadFondos } = useFondos();
  const { userId, companyId, refreshModels } = useAgentEnv();
  // El motor elegido en el menú del "+": lo mandan TODAS las tarjetas que generan, no sólo
  // la libre (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario).
  const proveedorForzado = useProveedorDeImagen();
  const {
    format,
    scene_summary,
    instruccion: instruccionSeed,
    autogenerate,
    product_ids: bakedProductIdsRaw,
    model_id: bakedModelIdRaw,
    pose_ids: bakedPoseIdsRaw,
    background: bakedBackgroundRaw,
  } = card.input as {
    format: string;
    scene_summary: string;
    instruccion?: string;
    autogenerate?: boolean;
    product_ids?: string[];
    model_id?: string | number;
    pose_ids?: string[];
    background?: { mode?: string; id?: number | string; text?: string };
  };
  // Valores "horneados": el agente los pasó directo (porque estaba seguro y no
  // mostró el picker). Si vienen, ganan sobre cualquier card de picker y se
  // mandan al request de fondo. null = no horneado → leemos del picker.
  const bakedProductIds =
    Array.isArray(bakedProductIdsRaw) && bakedProductIdsRaw.length > 0
      ? bakedProductIdsRaw
      : null;
  const bakedModelId =
    bakedModelIdRaw !== undefined && bakedModelIdRaw !== null && bakedModelIdRaw !== ''
      ? String(bakedModelIdRaw)
      : null;
  const bakedPoseIds =
    Array.isArray(bakedPoseIdsRaw) && bakedPoseIdsRaw.length > 0
      ? bakedPoseIdsRaw
      : null;
  const bakedBackground =
    bakedBackgroundRaw && bakedBackgroundRaw.mode ? bakedBackgroundRaw : null;
  // Sheet de edición abierto (tocar una fila del resumen para cambiarla).
  const [editing, setEditing] = useState<
    null | 'product' | 'model' | 'pose' | 'format' | 'fondo' | 'calidad'
  >(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  // Guard para que el auto-generar dispare una sola vez.
  const autoFiredRef = useRef(false);
  // Reanudar una generación interrumpida (recarga / pestaña cerrada).
  useResumeImageGeneration(card, chat, setGenerating);
  // Instrucción adicional opcional — local para tipear fluido; se persiste
  // en el input de la card al salir del campo. Se inicializa con lo que el
  // agente haya pre-cargado (si dedujo algo del prompt del usuario).
  const [instruccion, setInstruccion] = useState<string>(instruccionSeed || '');

  // Buscamos en las cards resueltas anteriores del chat para sacar los
  // datos que el agente NO nos pasó. Eso permite que confirm_generate
  // venga batcheado en el mismo turno que las picker tools — cuando el
  // user las va resolviendo, esta card se va llenando sola sin necesidad
  // de un round-trip extra al agente.
  const findLatest = (kind: V2ToolCard['kind']): V2ToolCard | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m.toolCards) continue;
      for (let j = m.toolCards.length - 1; j >= 0; j--) {
        const c = m.toolCards[j];
        // No cruzamos el límite de una generación previa ya completada: las
        // picker cards anteriores a OTRO confirm_generate resuelto pertenecen
        // a esa generación pasada, no a esta. Sin esto, una 2ª generación que
        // saltea (no muestra) un picker heredaría el valor de la 1ª en vez de
        // caer al default (modelo automático / sin pose).
        if (
          c.kind === 'confirm_generate' &&
          c.status === 'resolved' &&
          c.id !== card.id
        ) {
          return null;
        }
        if (c.kind === kind && c.status === 'resolved') return c;
      }
    }
    return null;
  };

  // Si un valor vino horneado, ignoramos su card de picker (incluso una vieja
  // de un turno anterior): el valor del agente manda.
  const productCard = bakedProductIds ? null : findLatest('pick_product');
  const modelCard = bakedModelId ? null : findLatest('pick_model');
  const poseCard = bakedPoseIds ? null : findLatest('pick_pose');
  const backgroundCard = bakedBackground ? null : findLatest('pick_background');

  const product_ids: string[] =
    bakedProductIds ?? productCard?.response?.product_ids ?? [];
  const products: MockProductV2[] = userProducts.filter((p) =>
    product_ids.includes(p.id),
  );

  // Modelo: response de pick_model (o el id horneado) → label + ModelSelection.
  const modelResp = modelCard?.response ?? (bakedModelId ? { model_id: bakedModelId } : undefined);
  let model_label: string | null = null;
  if (modelResp) {
    if (modelResp.auto) model_label = 'Automático';
    else if (modelResp.model_name) model_label = modelResp.model_name;
    else if (modelResp.model_id) {
      const m = userModels.find((mm) => String(mm.id) === modelResp.model_id);
      model_label = m?.nombre || null;
    } else if (modelResp.create_new) model_label = `${palabra.titulo} a medida`;
    else if (modelResp.upload_own) model_label = `${palabra.titulo} propio`;
  }
  const modelSelection: ModelSelection | null = (() => {
    if (!modelResp) return null;
    if (modelResp.auto) return { mode: 'auto' };
    if (modelResp.model_id) {
      const m = userModels.find((mm) => String(mm.id) === modelResp.model_id);
      return {
        mode: 'preset',
        id: modelResp.model_id,
        name: modelResp.model_name || m?.nombre || palabra.titulo,
        imageUrl: m?.thumbnailUrl || '',
      };
    }
    return null;
  })();

  const pose_ids: string[] = bakedPoseIds ?? poseCard?.response?.pose_ids ?? [];
  const { poses } = usePoses();
  const selectedPoses = poses.filter((p) => pose_ids.includes(p.id));

  // Fondo: del id horneado por el agente o de pick_background resuelto.
  const currentFondoSel: FondoSelection | null = bakedBackground
    ? agentBgToFondoSelection(bakedBackground, fondoBiblioteca, fondoGuardados)
    : (backgroundCard?.response?.fondo ?? null);
  const backgroundForGen = fondoSelectionToBackground(currentFondoSel);

  // Calidad de salida. Vive en el INPUT de la card (no en un `useState`) para que la
  // edición sobreviva al remonte y a la reanudación: `useResumeImageGeneration` relee el
  // input, y con un state la generación retomada salía en otra calidad que la del botón
  // (ADR-2026-09-13-la-calidad-de-imagen-se-elige-en-la-card). Cae a la preferencia
  // global del menú "+" y recién después al default (1K).
  const { image_size: calidadElegida, quality: calidadDelAgente } = card.input as {
    image_size?: string;
    quality?: string;
  };
  // `image_size` = lo que tocó el usuario en la fila y manda sobre todo. `quality` = lo
  // que preseleccionó el agente en la tool call. Después, la preferencia global.
  const calidad = normalizarResolucion(
    calidadElegida ?? calidadDelAgente ?? chat.preferenciasDeGeneracion().calidadImagen,
  );
  // Créditos = una imagen por pose (mínimo 1), por la tarifa de la calidad. Sin el
  // multiplicador el botón prometía 1 por imagen y el backend cobraba hasta 3.
  const cantidadDeImagenes = Math.max(1, pose_ids.length);
  const creditCost = cantidadDeImagenes * creditosPorResolucion(calidad);

  // ── Handlers de edición (patchean la card correspondiente) ──────────
  const applyProducts = (ids: string[]) => {
    if (productCard) {
      chat.patchToolCard(productCard.id, { response: { product_ids: ids } });
    } else {
      // Producto horneado (sin pick_product): editamos el input de esta card.
      chat.patchToolCard(card.id, { input: { ...card.input, product_ids: ids } });
    }
  };
  const applyModel = (sel: ModelSelection) => {
    if (modelCard) {
      let resp: any;
      if (sel.mode === 'auto') resp = { auto: true };
      else if (sel.mode === 'preset') resp = { model_id: sel.id, model_name: sel.name };
      else if (sel.mode === 'custom') resp = { create_new: true };
      else resp = { upload_own: true };
      chat.patchToolCard(modelCard.id, { response: resp });
      return;
    }
    // Modelo horneado (sin pick_model): editamos el input de esta card.
    // Solo preset/auto son representables como model_id; el resto cae a auto.
    const model_id = sel.mode === 'preset' ? sel.id : undefined;
    chat.patchToolCard(card.id, { input: { ...card.input, model_id } });
  };
  const applyPoses = (ids: string[]) => {
    const names = ids.map((id) => poses.find((p) => p.id === id)?.nombre || '');
    if (poseCard) {
      chat.patchToolCard(poseCard.id, { response: { pose_ids: ids, pose_names: names } });
    } else {
      // Pose horneada (sin pick_pose): editamos el input de esta card.
      chat.patchToolCard(card.id, { input: { ...card.input, pose_ids: ids } });
    }
  };
  // Lo que el usuario elige acá pasa a ser su DEFAULT: la próxima generación arranca con
  // esto puesto y el agente lo asume (ADR-2026-09-14-cambiar-el-formato-en-la-card-cambia-
  // el-default). Sólo cuando LO CAMBIA: aceptar lo que propuso el agente no toca nada.
  const applyFormat = (id: string) => {
    chat.patchToolCard(card.id, { input: { ...card.input, format: id } });
    chat.recordarPreferencia('formatoImagen', id);
  };
  const applyCalidad = (id: ResolucionImagen) => {
    chat.patchToolCard(card.id, { input: { ...card.input, image_size: id } });
    chat.recordarPreferencia('calidadImagen', id);
  };
  const applyFondo = (sel: FondoSelection) => {
    if (backgroundCard) {
      chat.patchToolCard(backgroundCard.id, { response: { fondo: sel } });
      return;
    }
    // Fondo horneado (sin pick_background): guardamos el shape del agente
    // (mode + id?/text?) en el input de esta card.
    let bg: any;
    if (sel.mode === 'biblioteca') bg = { mode: 'biblioteca', id: sel.id };
    else if (sel.mode === 'guardado') {
      const f = fondoGuardados.find((x) => x.fileName === sel.fileName);
      bg = { mode: 'guardado', id: f?.id };
    } else if (sel.mode === 'descripcion') bg = { mode: 'descripcion', text: sel.text };
    else bg = { mode: sel.mode };
    chat.patchToolCard(card.id, { input: { ...card.input, background: bg } });
  };

  // ── Generar de verdad ───────────────────────────────────────────────
  // Corre los guards de crédito y llama a `generateModeloImage` (misma API
  // que el modo modelo). En éxito, resuelve la card con los ids generados
  // → la card pasa a mostrar el resultado inline (GenerationResultView).
  const generarSinCandado = async () => {
    if (!active || generating) return;
    setGenError(null);
    if (!userId) {
      setGenError('No pudimos identificar al usuario. Probá recargar.');
      return;
    }
    if (product_ids.length === 0) {
      setGenError('Elegí al menos un producto.');
      return;
    }
    setGenerating(true);
    try {
      // Guard de créditos.
      if (companyId) {
        const { data: cp } = await supabase
          .from('company_profiles')
          .select('imagenes_restantes:imagenes_disponibles')
          .eq('id_company', companyId)
          .single();
        const left = Number((cp as any)?.imagenes_restantes ?? 0);
        if (left < creditCost) {
          setGenError(
            'No te alcanzan los créditos para esta generación. Sumá créditos para continuar.',
          );
          setGenerating(false);
          return;
        }
      }
      const { generateModeloImage } = await import('./agentGenerate');
      // Correlación para RECUPERAR la generación si el fetch muere (recarga /
      // pestaña cerrada): el pendiente se persiste en el input de la card
      // ANTES de llamar — al remontar, useResumeImageGeneration lo retoma.
      const executionId = crypto.randomUUID();
      if (companyId) {
        chat.patchToolCard(card.id, {
          input: {
            ...card.input,
            pending_generation: {
              execution_id: executionId,
              company_id: companyId,
              expected_count: Math.max(1, pose_ids.length),
              started_at: Date.now(),
            },
          },
        });
      }
      const { imageIds, companyId: companyIdResult } = await generateModeloImage(
        {
          userId,
          variantIds: product_ids,
          modelSelection,
          poseIds: pose_ids,
          formatSlug: format,
          // La de la card: ya resolvió preferencia y default.
          imageSize: calidad,
          instruccion: instruccion.trim() || undefined,
          background: backgroundForGen,
          companyId,
          ...(proveedorForzado ? { providerOverride: proveedorForzado } : {}),
          ...(companyId ? { executionId } : {}),
        },
      );
      // Persistir última config para preselección futura del agente.
      try {
        localStorage.setItem('atlas-v2-last-format', format);
        if (modelSelection)
          localStorage.setItem(
            'atlas-v2-last-model',
            JSON.stringify(modelSelection),
          );
        localStorage.setItem(
          'atlas-v2-last-products',
          JSON.stringify(product_ids),
        );
      } catch {
        /* noop */
      }
      sonarListo();
      // Imputar los créditos de las imágenes al tally del chat.
      chat.addChatCredits(imageIds.length);
      // Resuelve la card con los ids → render del resultado inline.
      chat.resolveToolCard(card.id, {
        confirmed: true,
        image_ids: imageIds,
        company_id: companyIdResult,
      });
    } catch (e: any) {
      console.error('[ConfirmGenerate] generación falló', e);
      // Fallo definitivo (el poll también agotó) → nada que retomar.
      chat.patchToolCard(card.id, {
        input: { ...card.input, pending_generation: null },
      });
      setGenError(e?.message || 'No se pudo generar. Probá de nuevo.');
      setGenerating(false);
    }
  };
  /**
   * El cerrojo de doble click, ANTES de cualquier `await`
   * (ADR-2026-09-14-generar-una-sola-vez-por-click). `generating` es estado: dos clicks
   * en el mismo tick lo ven en false los dos, y la consulta de saldo de adentro tarda lo
   * suficiente para que el segundo pase. Es el mismo bug —y el mismo cerrojo por id de
   * card— que ya tuvo el lote (ADR-2026-09-13-el-lote-se-lanzaba-dos-veces). Cubre también
   * el botón "Generar sin preguntarme", que llama a este mismo handler.
   */
  const handleGenerate = async () => {
    if (!reservarLanzamiento(card.id)) return;
    try {
      await generarSinCandado();
    } finally {
      liberarLanzamiento(card.id);
    }
  };

  // Auto-generar: si el agente marcó autogenerate y la card ya está activa
  // (es decir, todas las picks previas se resolvieron y tenemos producto),
  // disparamos solos — sin el click de confirmación del usuario.
  const yaDisparadaModelo = !!(card.input as any)?.pending_generation;
  useEffect(() => {
    if (
      autogenerate &&
      active &&
      card.status !== 'resolved' &&
      !autoFiredRef.current &&
      !yaDisparadaModelo &&
      product_ids.length > 0
    ) {
      autoFiredRef.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autogenerate, active, card.status, product_ids.length]);

  // Avisa hacia arriba si está mostrando la espera. Va ANTES de cualquier
  // return: es un hook y su orden no puede depender del estado de la card.
  const mostrandoCarga = generating || (autogenerate && !genError);
  useEffect(() => {
    onCargandoChange?.(!!mostrandoCarga);
  }, [mostrandoCarga, onCargandoChange]);

  // Card resuelta: si tiene ids generados, mostramos el resultado inline;
  // sino (edge) un estado mínimo.
  if (card.status === 'resolved') {
    const ids: number[] = card.response?.image_ids || [];
    const cid: string = card.response?.company_id || companyId || '';
    if (ids.length > 0) {
      return <GenerationResultView imageIds={ids} companyId={cid} onAgregar={chat.addAttachment} />;
    }
    return (
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-brand-primary" />
        </div>
        <p className="text-[14px] text-brand-black/85 leading-snug">Listo.</p>
      </div>
    );
  }

  // Mientras genera (manual o auto, sin error), mostramos la animación de
  // carga en la card — no el resumen. Si falla, caemos al resumen para
  // reintentar/editar. Es la MISMA animación que la columna de resultados del modo
  // Modelo, un recuadro por imagen esperada
  // (ADR-2026-09-09-las-cards-del-chat-generan-como-el-panel).
  if (generating || (autogenerate && !genError)) {
    const pendiente = (card.input as any)?.pending_generation as
      | PendingGenerationInput
      | undefined;
    return (
      <GenerandoEnCard
        cantidad={pendiente?.expected_count ?? 1}
        format={format}
        creadaEn={pendiente?.started_at}
      />
    );
  }

  // Fila editable del resumen — toda la fila es clickeable y abre el sheet
  // correspondiente. Chevron a la derecha como affordance.
  const Row: React.FC<{
    label: string;
    onEdit?: () => void;
    children: React.ReactNode;
  }> = ({ label, onEdit, children }) => (
    <button
      type="button"
      onClick={() => onEdit && active && onEdit()}
      disabled={!active || !onEdit}
      className="flex items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg text-left transition-colors enabled:hover:bg-neutral-50 disabled:cursor-default"
    >
      <span className="w-16 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
        {label}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
      {onEdit && (
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/35 shrink-0" />
      )}
    </button>
  );

  return (
    <div className="p-5">
      <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
        Resumen
      </p>
      <p className="text-[15px] font-semibold text-brand-black/90 mb-4 leading-snug">
        {scene_summary}
      </p>

      <div className="flex flex-col gap-1 mb-5">
        {/* Productos */}
        <Row
          label={`Producto${products.length > 1 ? 's' : ''}`}
          onEdit={product_ids.length > 0 ? () => setEditing('product') : undefined}
        >
          <div className="flex flex-wrap gap-1.5">
            {products.map((p) => (
              <span
                key={p.id}
                className="flex items-center gap-1.5 bg-[#F7F7F9] rounded-full pr-3 pl-1 py-0.5"
              >
                <CachedImg src={p.imageUrl} alt={p.name} className="w-5 h-5 rounded-full object-cover" />
                <span className="text-[12.5px] font-medium text-brand-black/85 truncate max-w-[140px]">
                  {p.name}
                </span>
              </span>
            ))}
          </div>
        </Row>
        {/* Modelo — si hay card o id horneado (si va automático, no mostramos) */}
        {(modelCard || bakedModelId) && (
          <Row label={palabra.titulo} onEdit={() => setEditing('model')}>
            <span className="text-[13px] font-medium text-brand-black/85">
              {model_label || 'Automático'}
            </span>
          </Row>
        )}
        {/* Poses — solo si hay (si el agente salteó la pose, no mostramos fila) */}
        {(poseCard || pose_ids.length > 0) && (
          <Row
            label={`Pose${selectedPoses.length > 1 ? 's' : ''}`}
            onEdit={pose_ids.length > 0 || poseCard ? () => setEditing('pose') : undefined}
          >
            <span className="text-[13px] font-medium text-brand-black/85">
              {selectedPoses.length > 0
                ? selectedPoses.map((p) => p.nombre).join(', ')
                : pose_ids.join(', ')}
            </span>
          </Row>
        )}
        {/* Formato */}
        <Row label="Formato" onEdit={() => setEditing('format')}>
          <span className="text-[13px] font-medium text-brand-black/85">
            {getFormatLabel(format) || format}
          </span>
        </Row>
        {/* Calidad — el badge del botón se mueve con esto */}
        <Row label="Calidad" onEdit={() => setEditing('calidad')}>
          <span className="text-[13px] font-medium text-brand-black/85">
            {labelResolucion(calidad)}
          </span>
        </Row>
        {/* Fondo — solo si el agente lo decidió (sino va automático) */}
        {currentFondoSel && (
          <Row label="Fondo" onEdit={() => setEditing('fondo')}>
            <span className="text-[13px] font-medium text-brand-black/85 truncate block max-w-[220px]">
              {currentFondoSel.mode === 'descripcion'
                ? currentFondoSel.text || 'Descripción'
                : fondoSelectionLabel(currentFondoSel)}
            </span>
          </Row>
        )}
      </div>

      {/* Instrucción adicional (opcional) — igual que el modo modelo. Se
          guarda en el input de la card al salir del campo (onBlur). */}
      <div className="relative mb-4">
        <input
          type="text"
          value={instruccion}
          onChange={(e) => setInstruccion(e.target.value)}
          onBlur={() =>
            chat.patchToolCard(card.id, {
              input: { ...card.input, instruccion: instruccion.trim() },
            })
          }
          disabled={!active}
          placeholder="Instrucción adicional (opcional)"
          className="w-full h-11 pl-4 pr-[104px] rounded-2xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
        />
        <PromptRecientesButton
          tipo="instruccion"
          onPick={(texto) => {
            setInstruccion(texto);
            // Además de llenar el campo hay que persistirlo en la card: el input
            // solo guarda en el `onBlur`, que acá nunca se dispara.
            chat.patchToolCard(card.id, {
              input: { ...card.input, instruccion: texto },
            });
          }}
          titulo="Instrucciones usadas antes"
          className="absolute right-2 top-1/2 -translate-y-1/2"
        />
      </div>

      {genError && (
        <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {genError}
        </div>
      )}

      <button
        type="button"
        onClick={handleGenerate}
        disabled={!active || generating}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
      >
        {generating ? (
          <>
            <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            <span>Generando…</span>
          </>
        ) : (
          <>
            <Sparkles className="w-4.5 h-4.5" />
            <span>Generar</span>
            <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
              <img
                src="/images/token_icon.png"
                alt=""
                aria-hidden="true"
                className="w-3.5 h-3.5 object-contain"
              />
              {creditCost}
            </span>
          </>
        )}
      </button>

      {/* Genera y apaga la confirmación de las imágenes para las próximas. No aparece
          si la card se está autogenerando: ahí no hay confirmación que sacar. */}
      {!autogenerate && (
        <BotonGenerarSinPreguntar
          tipo="imagen"
          noPreguntarMas={chat.noPreguntarMas}
          onAccion={handleGenerate}
          disabled={!active || generating}
        />
      )}

      {/* ── Sheets de edición ─────────────────────────────────────────── */}
      {editing === 'product' && (
        <ProductFullMenu
          initialSelected={product_ids}
          allowMulti
          onCancel={() => setEditing(null)}
          onAccept={(ids) => {
            applyProducts(ids);
            setEditing(null);
          }}
        />
      )}
      <ModelPickerSheet
        open={editing === 'model'}
        selection={modelSelection}
        onChange={applyModel}
        onClose={() => setEditing(null)}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
      />
      <PosePickerSheet
        open={editing === 'pose'}
        poses={poses}
        selectedIds={pose_ids}
        onChange={applyPoses}
        onClose={() => setEditing(null)}
      />
      <FormatPicker
        open={editing === 'format'}
        selectedId={format}
        onChange={applyFormat}
        onClose={() => setEditing(null)}
      />
      <CalidadImagenSheet
        open={editing === 'calidad'}
        seleccionada={calidad}
        onChange={applyCalidad}
        onClose={() => setEditing(null)}
        cantidadDeImagenes={cantidadDeImagenes}
      />
      <FondoPickerSheet
        open={editing === 'fondo'}
        selection={currentFondoSel}
        onChange={(sel) => {
          applyFondo(sel);
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
        biblioteca={fondoBiblioteca}
        guardados={fondoGuardados}
        companyId={companyId}
        onFondoSaved={reloadFondos}
        selectedProductNames={products.map((p) => p.name)}
      />
    </div>
  );
};

/** Una toma de la tanda: su prompt y su formato. */

// ─── FreeformGenerateCard (flujo imagen libre) ─────────────────────────
// Genera una imagen a partir de un prompt libre (sin las restricciones del
// flujo producto-sobre-modelo). El prompt es editable; al generar usa la
// misma API que el Estudio de Video (`generateFreeformImage`). Resultado
// inline (GenerationResultView).

const FreeformGenerateCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
  /** Igual que en `ConfirmGenerateCard`: avisa cuándo está mostrando la espera,
   *  para que el recuadro blanco se achique a las cajas de carga. */
  onCargandoChange?: (cargando: boolean) => void;
}> = ({ card, active, chat, onCargandoChange }) => {
  const { userId, companyId } = useAgentEnv();
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  // La tuerca del Chat (cuentas dev): fuerza el proveedor de imagen. Null = producción.
  const proveedorForzado = useProveedorDeImagen();
  const {
    prompt: promptSeed,
    format: formatSeed,
    cantidad: cantidadSeed,
    autogenerate,
    tarea,
    edicion,
    piel_expuesta: pielExpuesta,
    fondo_blanco: fondoBlanco,
    luz_fondo_blanco: luzFondoBlanco,
  } = card.input as {
    prompt?: string;
    prompts?: string[];
    format?: string;
    cantidad?: number;
    autogenerate?: boolean;
    /** Lo que declaró el agente; eligen el motor en el backend
     *  (ADR-2026-09-13-el-agente-elige-el-motor-por-la-tarea). No se muestra. */
    tarea?: 'crear' | 'editar';
    edicion?: 'conservar' | 'fusionar';
    piel_expuesta?: boolean;
    fondo_blanco?: boolean;
    luz_fondo_blanco?: 'difusa' | 'dura';
  };
  // Cuántas imágenes pide la card. Lo manda el agente (tool `cantidad`) o el modo
  // directo desde su control +/-, y el usuario lo cambia en la fila "Cantidad" de la card
  // (se patchea en el input, como el formato, para que sobreviva a la reanudación —
  // ADR-2026-09-25-la-cantidad-se-cambia-en-la-card). El clamp REAL lo hace
  // `generateFreeformImage` (FREEFORM_MAX_CANTIDAD = 6); acá lo espejamos para el costo.
  const cantidad = Math.max(1, Math.min(6, Math.floor(cantidadSeed ?? 1)));
  // Referencias elegidas por el AGENTE (producto/modelo/logo/generada) —
  // hacen viajar la foto REAL al backend. Sin esto, un prompt que dice
  // "the reference product image" generaba un producto inventado (ADR-586).
  const refSpecs: ImagenRefSpec[] = Array.isArray(
    (card.input as any).reference_images,
  )
    ? ((card.input as any).reference_images as ImagenRefSpec[])
    : [];
  // Sin ids adentro: el agente a veces copia el `image_id=…` del marcador al prompt y el
  // motor no sabe qué es (ADR-2026-09-25-los-ids-no-salen-en-el-prompt-ni-al-usuario).
  const [prompt, setPrompt] = useState<string>(limpiarIdsDelPrompt(promptSeed || ''));
  // Las referencias que van a viajar, como fichas con miniatura: al editar una imagen se
  // ve CUÁL (ADR-2026-09-25-las-referencias-se-ven-en-la-card).
  const fichasDeReferencias = useMiniaturasDeReferencias({
    specs: refSpecs as SpecDeReferencia[],
    adjuntasActuales: chat.getReferenceImages(),
    mensajes: chat.messages,
    companyId,
    userId,
    productos: userProducts,
    modelos: userModels,
    palabraModelo: palabraDeModeloActual().titulo,
  });
  // Sin formato en la tool call, el preferido por el usuario (menú "+" → Preferencias de contenido)
  // antes que el genérico (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas).
  const [format, setFormat] = useState<string>(
    formatSeed || chat.preferenciasDeGeneracion().formatoImagen || 'post',
  );
  const [editingFormat, setEditingFormat] = useState(false);
  const [editingCalidad, setEditingCalidad] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  // Autosize del prompt: crece hasta donde haga falta, sin scroll interno.
  // Corre también al montar (no solo al tipear) porque el prompt llega
  // completo desde el agente.
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [prompt]);
  // Guard para que el auto-generar dispare una sola vez.
  const autoFiredRef = useRef(false);
  // Reanudar una generación interrumpida (recarga / pestaña cerrada).
  useResumeImageGeneration(card, chat, setGenerating);

  // Calidad de salida: del input de la card (no de un `useState`, para que la edición
  // sobreviva a la reanudación — ver el comentario gemelo en `ConfirmGenerateCard`),
  // con la preferencia global de fallback.
  const { image_size: calidadElegida, quality: calidadDelAgente } = card.input as {
    image_size?: string;
    quality?: string;
  };
  // `image_size` = lo que tocó el usuario en la fila y manda sobre todo. `quality` = lo
  // que preseleccionó el agente en la tool call. Después, la preferencia global.
  const calidad = normalizarResolucion(
    calidadElegida ?? calidadDelAgente ?? chat.preferenciasDeGeneracion().calidadImagen,
  );
  const creditCost = cantidad * creditosPorResolucion(calidad);

  const generarSinCandado = async () => {
    if (!active || generating) return;
    setGenError(null);
    if (!userId) {
      setGenError('No pudimos identificar al usuario. Probá recargar.');
      return;
    }
    // Lo que se manda también sale sin ids, por si se pegó uno al editar el prompt
    // (revisión de Greptile al PR #146).
    const promptFinal = limpiarIdsDelPrompt(prompt.trim());
    if (!promptFinal) {
      setGenError('Escribí qué imagen querés generar.');
      return;
    }
    // Una referencia "adjunto" que no existe corta ACÁ, antes de gastar: si no, la
    // generación salía sólo con el texto y el agente creía que había usado la foto
    // (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir). La card queda
    // resuelta con el motivo: el agente se lo pide al usuario y vuelve a llamar.
    const falta = adjuntaQueFalta(
      refSpecs,
      chat.getReferenciasEtiquetadas().filter((r) => r.tipo === 'adjunto').length,
      chat.messages,
    );
    if (falta) {
      setGenError(falta);
      chat.resolveToolCard(card.id, { confirmed: false, error: falta });
      return;
    }
    setGenerating(true);
    try {
      if (companyId) {
        const { data: cp } = await supabase
          .from('company_profiles')
          .select('imagenes_restantes:imagenes_disponibles')
          .eq('id_company', companyId)
          .single();
        const left = Number((cp as any)?.imagenes_restantes ?? 0);
        if (left < creditCost) {
          setGenError(
            'No te alcanzan los créditos para esta generación. Sumá créditos para continuar.',
          );
          setGenerating(false);
          return;
        }
      }
      const { generateFreeformImage } = await import('./agentGenerate');
      // Referencias = adjuntos del usuario (menú "+") + las que eligió el
      // agente (producto/modelo/logo/generada, resueltas a su foto REAL).
      // El producto va en tamaño FULL (el thumbnail de 300px del catálogo es
      // pobre como referencia de fidelidad). Cada una entra ETIQUETADA: si se pasan del
      // tope, cae primero el fondo y nunca el producto
      // (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad).
      // Las adjuntas entran solas; el agente sólo puede ETIQUETARLAS (qué es cada una:
      // "Pose: …", "Producto: …") — ADR-2026-09-22-el-agente-etiqueta-lo-que-adjunta-el-usuario.
      const refs: ReferenciaEtiquetada[] = etiquetarAdjuntas(
        chat.getReferenciasEtiquetadas(),
        refSpecs.filter((s) => s.source === 'adjunto' && !s.adjunto_id),
      );
      const conEtiqueta = (r: ReferenciaEtiquetada, spec: ImagenRefSpec): ReferenciaEtiquetada =>
        spec.etiqueta?.trim() ? { ...r, etiqueta: spec.etiqueta.trim() } : r;
      let modeloSinFoto = false;
      for (const spec of refSpecs) {
        try {
          if (spec.source === 'adjunto' && spec.adjunto_id) {
            // Una imagen subida en un mensaje anterior: ya no viaja en el historial, pero
            // sigue en Storage y el marcador le dio un id
            // (ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id).
            const a = adjuntoPorId(chat.messages, spec.adjunto_id);
            const u = a ? await signReferenceImage(a.storagePath, a.bucket) : null;
            if (u) refs.push(conEtiqueta({ url: u, tipo: 'adjunto' }, spec));
          } else if (spec.source === 'producto' && spec.product_id && companyId) {
            const full = await getProductFullImageUrl(
              companyId,
              spec.product_id,
            );
            const u =
              full ||
              userProducts.find((p) => p.id === spec.product_id)?.imageUrl;
            // Con `referenciaDeProducto`: si lo que hay es un link de afuera —un producto
            // recién traído de Tiendanube, cuya foto sigue en el CDN de la tienda— viaja
            // sólo el id y el backend baja la foto. Mandando ese link, la generación
            // ENTERA rebota con 400
            // (ADR-2026-09-21-el-producto-recien-traido-viaja-por-id-y-no-por-link).
            if (u) refs.push(referenciaDeProducto(u, spec.product_id));
          } else if (spec.source === 'modelo') {
            // Re-firmada acá y no la `thumbnailUrl` de la lista: ésa se firmó al cargar
            // y dura una hora; en un chat largo llegaba vencida al worker
            // (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
            const m = userModels.find((mm) => String(mm.id) === String(spec.model_id));
            const u = m ? await fotoDeModeloParaReferencia(m) : null;
            // Sin la foto del modelo elegido no se genera: saldría otra persona y se
            // cobraría igual (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
            if (m && !u) modeloSinFoto = true;
            if (u) refs.push({ url: u, tipo: 'modelo' });
          } else if (spec.source === 'logo' && companyId) {
            const b = await fetchBrandContext(userId, companyId);
            if (b?.logoUrl) refs.push(referenciaDeLogo(b.logoUrl));
          } else if (spec.source === 'generada' && spec.image_id && companyId) {
            // UUID → tal cual, sin Number() (rompería el match).
            const imgs = await preloadGeneratedImages(
              [spec.image_id],
              companyId,
            );
            if (imgs[0]?.signedUrl)
              refs.push(conEtiqueta({ url: imgs[0].signedUrl, tipo: 'generada' }, spec));
          } else if (spec.source === 'pose' && spec.pose_id && companyId) {
            // Una pose GUARDADA por id: el agente la puede elegir sola o reusar la que el
            // usuario enganchó más arriba (ADR-2026-09-14-la-pose-y-el-fondo-son-
            // referencias-por-id). Las que enganchó en ESTE mensaje ya entraron por
            // `getReferenciasEtiquetadas`; `priorizarReferencias` deduplica por URL.
            const u = await urlDePoseGuardada(spec.pose_id, companyId);
            if (u) refs.push({ url: u, tipo: 'pose' });
          } else if (spec.source === 'fondo' && spec.fondo_id) {
            const u = await urlDeFondoGuardado(spec.fondo_id, companyId ?? '');
            if (u) refs.push({ url: u, tipo: 'fondo' });
          }
          // 'adjunto' ya está cubierto por getReferenciasEtiquetadas() + etiquetarAdjuntas.
        } catch (refErr) {
          console.warn('[FreeformGenerate] referencia no resuelta', spec, refErr);
        }
      }
      if (modeloSinFoto) throw new ReferenciaIrrecuperable(MENSAJE_MODELO_SIN_FOTO);
      // Con las firmas por vencer renovadas: entre armar la tarjeta y tocar Generar puede
      // pasar más de una hora (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
      const referenceImages = await conReferenciasFrescas(priorizarReferencias(refs));
      // Correlación para RECUPERAR la generación si el fetch muere (recarga /
      // pestaña cerrada): el pendiente se persiste en el input de la card
      // ANTES de llamar — al remontar, useResumeImageGeneration lo retoma.
      const executionId = crypto.randomUUID();
      if (companyId) {
        chat.patchToolCard(card.id, {
          input: {
            ...card.input,
            // El prompt que SALE (el usuario pudo editarlo en la card): es lo que el
            // registro del historial le cuenta al agente en los turnos siguientes.
            // Sin esto quedaba el que propuso el agente, no el que generó
            // (ADR-2026-09-13-el-registro-lleva-lo-que-genero-de-verdad).
            prompt: promptFinal,
            pending_generation: {
              execution_id: executionId,
              company_id: companyId,
              expected_count: cantidad,
              started_at: Date.now(),
            },
          },
        });
      }
      const { imageIds, companyId: companyIdResult } =
        await generateFreeformImage({
          userId,
          prompt: promptFinal,
          formatSlug: format,
          cantidad,
          companyId,
          // La de la card: ya resolvió preferencia y default (1K).
          imageSize: calidad,
          ...(tarea ? { tarea } : {}),
          ...(edicion === 'fusionar' ? { edicion } : {}),
          ...(pielExpuesta ? { pielExpuesta: true } : {}),
          ...(fondoBlanco ? { fondoBlanco: true, ...(luzFondoBlanco ? { luzFondoBlanco } : {}) } : {}),
          ...(referenceImages.length > 0 ? { referenceImages } : {}),
          ...(companyId ? { executionId } : {}),
          ...(proveedorForzado ? { providerOverride: proveedorForzado } : {}),
        });
      try {
        localStorage.setItem('atlas-v2-last-format', format);
      } catch {
        /* noop */
      }
      sonarListo();
      chat.addChatCredits(imageIds.length);
      chat.resolveToolCard(card.id, {
        confirmed: true,
        image_ids: imageIds,
        company_id: companyIdResult,
      });
    } catch (e: any) {
      console.error('[FreeformGenerate] generación falló', e);
      // Fallo definitivo (el poll también agotó) → nada que retomar.
      chat.patchToolCard(card.id, {
        input: { ...card.input, pending_generation: null },
      });
      setGenError(e?.message || 'No se pudo generar. Probá de nuevo.');
      setGenerating(false);
    }
  };
  /**
   * El cerrojo de doble click, ANTES de cualquier `await`
   * (ADR-2026-09-14-generar-una-sola-vez-por-click). `generating` es estado: dos clicks
   * en el mismo tick lo ven en false los dos, y la consulta de saldo de adentro tarda lo
   * suficiente para que el segundo pase. Es el mismo bug —y el mismo cerrojo por id de
   * card— que ya tuvo el lote (ADR-2026-09-13-el-lote-se-lanzaba-dos-veces). Cubre también
   * el botón "Generar sin preguntarme", que llama a este mismo handler.
   */
  const handleGenerate = async () => {
    if (!reservarLanzamiento(card.id)) return;
    try {
      await generarSinCandado();
    } finally {
      liberarLanzamiento(card.id);
    }
  };

  // Guarda PERSISTENTE contra el doble disparo. `autoFiredRef` vive en la
  // instancia, así que un remontaje la resetea y la card volvía a generar
  // (dos execution_id distintos para un solo pedido). `pending_generation`
  // vive en el INPUT de la card, o sea en el estado del chat: sobrevive el
  // remontaje. Si ya está, la generación está en curso o terminó —
  // `useResumeImageGeneration` se encarga de retomarla poleando, no
  // generando de nuevo.
  // En la tanda no hay `pending_generation` (ver handleGenerate), así que la
  // marca de "ya arrancó" es propia.
  const yaDisparada =
    !!(card.input as any)?.pending_generation ||
    !!(card.input as any)?.batch_started;
  // Auto-generar: si el agente marcó autogenerate y la card está activa,
  // disparamos la generación apenas aparece — sin esperar el click del user.
  useEffect(() => {
    if (
      autogenerate &&
      active &&
      card.status !== 'resolved' &&
      !autoFiredRef.current &&
      !yaDisparada &&
      prompt.trim()
    ) {
      autoFiredRef.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autogenerate, active, card.status]);

  // Avisa hacia arriba si está mostrando la espera. Va ANTES de cualquier
  // return: es un hook y su orden no puede depender del estado de la card.
  const mostrandoCarga = generating || (autogenerate && !genError);
  useEffect(() => {
    onCargandoChange?.(!!mostrandoCarga);
  }, [mostrandoCarga, onCargandoChange]);

  if (card.status === 'resolved') {
    const ids: number[] = card.response?.image_ids || [];
    const cid: string = card.response?.company_id || companyId || '';
    if (ids.length > 0) {
      return <GenerationResultView imageIds={ids} companyId={cid} onAgregar={chat.addAttachment} />;
    }
    return (
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-brand-primary" />
        </div>
        <p className="text-[14px] text-brand-black/85 leading-snug">Listo.</p>
      </div>
    );
  }

  // Mientras genera (manual o auto, sin error), mostramos la animación de
  // carga en la card — no el editor del prompt. Si falla, caemos al form
  // para reintentar/editar. Misma animación que el panel del modo Modelo
  // (ADR-2026-09-09-las-cards-del-chat-generan-como-el-panel).
  if (generating || (autogenerate && !genError)) {
    const pendiente = (card.input as any)?.pending_generation as
      | PendingGenerationInput
      | undefined;
    return (
      <GenerandoEnCard
        cantidad={cantidad}
        format={format}
        creadaEn={pendiente?.started_at}
      />
    );
  }

  return (
    <div className="p-5">
      <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
        Prompt de imagen
      </p>
      {/* Sin caja propia (ni borde ni fondo) y sin scroll: el prompt se lee
          como el cuerpo del mensaje y crece hasta donde haga falta. El
          autosize de abajo le fija el alto al scrollHeight, sin tope — con
          `rows` fijo, un prompt largo (los usuarios pegan de 3.000 chars)
          quedaba en un cuadradito con scroll adentro de otro scroll. */}
      <textarea
        ref={promptRef}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onBlur={() =>
          chat.patchToolCard(card.id, {
            input: { ...card.input, prompt: prompt.trim() },
          })
        }
        disabled={!active || generating}
        rows={1}
        placeholder="Describí la imagen que querés…"
        className="w-full resize-none overflow-hidden bg-transparent border-none outline-none p-0 mb-4 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 leading-relaxed disabled:opacity-50"
      />

      {/* Referencias que viajan (sólo lectura: se cambian pidiéndoselo al agente). */}
      {fichasDeReferencias.length > 0 && (
        <div className="flex items-start gap-3 w-full -mx-2 px-2 py-1.5">
          <span className="w-16 pt-1 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
            Refs.
          </span>
          <div className="flex-1 min-w-0">
            <ChipsDeReferencias fichas={fichasDeReferencias} />
          </div>
        </div>
      )}

      {/* Formato */}
      <button
        type="button"
        onClick={() => active && setEditingFormat(true)}
        disabled={!active}
        className="flex items-center gap-3 w-full -mx-2 px-2 py-1.5 rounded-lg text-left transition-colors enabled:hover:bg-neutral-50"
      >
        <span className="w-16 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
          Formato
        </span>
        <span className="flex-1 text-[13px] font-medium text-brand-black/85">
          {getFormatLabel(format) || format}
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/35 shrink-0" />
      </button>

      <button
        type="button"
        onClick={() => active && setEditingCalidad(true)}
        disabled={!active}
        className="flex items-center gap-3 w-full -mx-2 px-2 py-1.5 rounded-lg text-left transition-colors enabled:hover:bg-neutral-50"
      >
        <span className="w-16 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
          Calidad
        </span>
        <span className="flex-1 text-[13px] font-medium text-brand-black/85">
          {labelResolucion(calidad)}
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/35 shrink-0" />
      </button>

      {/* Cantidad: el mismo control −/+ del composer, 1 a 6. El costo del botón lo sigue. */}
      <div className="flex items-center gap-3 w-full -mx-2 px-2 py-1.5 mb-4">
        <span className="w-16 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
          Cantidad
        </span>
        <div className={`flex-1 text-[13px] ${!active || generating ? 'opacity-50 pointer-events-none' : ''}`}>
          <CantidadInline
            cantidad={cantidad}
            onChangeCantidad={(n) =>
              chat.patchToolCard(card.id, { input: { ...card.input, cantidad: n } })
            }
          />
        </div>
      </div>

      {genError && (
        <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {genError}
        </div>
      )}

      <button
        type="button"
        onClick={handleGenerate}
        disabled={!active || generating}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
      >
        {generating ? (
          <>
            <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            <span>Generando…</span>
          </>
        ) : (
          <>
            <Sparkles className="w-4.5 h-4.5" />
            <span>Generar</span>
            <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
              <img
                src="/images/token_icon.png"
                alt=""
                aria-hidden="true"
                className="w-3.5 h-3.5 object-contain"
              />
              {creditCost}
            </span>
          </>
        )}
      </button>

      {!autogenerate && (
        <BotonGenerarSinPreguntar
          tipo="imagen"
          noPreguntarMas={chat.noPreguntarMas}
          onAccion={handleGenerate}
          disabled={!active || generating}
        />
      )}

      <FormatPicker
        open={editingFormat}
        selectedId={format}
        onChange={(id) => {
          setFormat(id);
          chat.patchToolCard(card.id, { input: { ...card.input, format: id } });
          chat.recordarPreferencia('formatoImagen', id);
        }}
        onClose={() => setEditingFormat(false)}
      />
      <CalidadImagenSheet
        open={editingCalidad}
        seleccionada={calidad}
        onChange={(id) => {
          chat.patchToolCard(card.id, { input: { ...card.input, image_size: id } });
          chat.recordarPreferencia('calidadImagen', id);
        }}
        onClose={() => setEditingCalidad(false)}
        cantidadDeImagenes={cantidad}
      />
    </div>
  );
};

// Sheet para editar el prompt de un frame "generar" del video — mismo shell
// que VideoConfigSheet (bottom sheet en mobile, panel derecho en desktop).
// El valor se persiste al cerrar (Listo o backdrop) desde el caller.
const FramePromptSheet: React.FC<{
  open: boolean;
  title: string;
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
}> = ({ open, title, value, onChange, onClose }) => {
  const { mounted, animated } = useExitTransition(open, 300);
  if (!mounted) return null;
  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 flex-1">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <p className="text-[13px] text-[#3C3C43]/70 leading-snug mb-3">
            Describí la imagen que se genera para este frame — el video arranca
            (o termina) exactamente en esa escena.
          </p>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={6}
            placeholder="Describí la imagen del frame…"
            className="w-full resize-none px-4 py-3 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
          />
        </div>
        <div className="px-5 pt-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-semibold shadow-sm transition-colors"
          >
            Listo
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

// ─── MediaGenerateCard (audio / video inline) ──────────────────────────
// Genera audio (voz/música) o video corto reusando los webhooks del Estudio
// de Video. El AUDIO autogenera si el agente lo marcó; el VIDEO nunca — el
// usuario siempre confirma desde un resumen (prompt, formato, duración,
// calidad, frames) que muestra el costo en créditos antes de generar. El
// resultado se reproduce inline (<audio> / <video>). El backend descuenta
// los créditos de la generación.

// Spec de un frame (primer/último) del video: de dónde sale la imagen.
// Fuentes vigentes: 'generar' (se crea una imagen apta), 'adjunto', o
// 'generada' (una imagen YA generada en el chat, por su image_id). Las fotos
// crudas de producto/modelo/logo NO son aptas como frame (van como referencia)
// — quedan en el type solo por cards viejas persistidas; el código las ignora.
type VideoFrameSpec = {
  source: 'generar' | 'producto' | 'modelo' | 'logo' | 'adjunto' | 'generada';
  prompt?: string;
  product_id?: string;
  model_id?: string;
  image_id?: string;
  /** Con "adjunto": una imagen subida en un mensaje anterior, por el id de su marcador
   *  (ADR-2026-09-24-adjunto-id-en-video). Sin esto, la primera adjunta del mensaje. */
  adjunto_id?: string;
};

// Spec de una imagen de REFERENCIA del video: guía el look/estilo sin fijar
// frames. Acá sí van producto/modelo/logo directos (y generadas del chat).
type VideoRefSpec = {
  source: 'producto' | 'modelo' | 'logo' | 'adjunto' | 'generada';
  product_id?: string;
  model_id?: string;
  image_id?: string;
  /** Qué es la imagen, dicho por el agente; con source "adjunto", `imagen` dice cuál
   *  ("Imagen N"). El mismo par que la imagen
   *  (ADR-2026-09-24-las-referencias-del-video-llevan-la-etiqueta-del-agente). */
  etiqueta?: string;
  imagen?: number;
  /** Con "adjunto": una imagen subida en un mensaje ANTERIOR, por el id de su marcador.
   *  Mismo campo que en imagen (ADR-2026-09-24-adjunto-id-en-video). */
  adjunto_id?: string;
};

/** El video adjunto a copiar EDITÁNDOLO (ADR-2026-09-24-copiar-un-video-es-editarlo). */
type VideoReferenciaSpec = {
  archivo?: string;
  desde?: number;
  /** editar (cambia sólo lo pedido), copiar (misma estructura, otra persona y lugar),
   *  camara (otras tomas) o referencia (un video NUEVO que toma el estilo del adjunto,
   *  sólo si el usuario lo pide textualmente —
   *  ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente). */
  modo?: 'editar' | 'copiar' | 'camara' | 'referencia';
};

/**
 * Spec de una referencia de IMAGEN. Es la del video más la pose y el fondo guardados
 * (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id): el video no los acepta —su
 * proveedor no tiene dónde ponerlos— así que son tipos distintos y no uno solo con campos
 * de más. Hasta acá la imagen reusaba el del video, que es exactamente por qué no se veía
 * que le faltaban dos fuentes.
 */
type ImagenRefSpec = {
  source: VideoRefSpec['source'] | 'pose' | 'fondo';
  product_id?: string;
  model_id?: string;
  image_id?: string;
  pose_id?: string;
  fondo_id?: string;
  /** Qué es la imagen, dicho por el agente; con source "adjunto", `imagen` dice cuál
   *  ("Imagen N") — ADR-2026-09-22-el-agente-etiqueta-lo-que-adjunta-el-usuario. */
  etiqueta?: string;
  imagen?: number;
  /** Una imagen subida en un mensaje ANTERIOR, por el id del marcador ("m1-2")
   *  — ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id. */
  adjunto_id?: string;
};

const MediaGenerateCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
  mediaType: 'audio' | 'video';
}> = ({ card, active, chat, mediaType }) => {
  const { userId, companyId } = useAgentEnv();
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  const input = card.input as {
    text?: string;
    audio_type?: 'voz' | 'musica' | 'efecto';
    /** Sólo música: segundos (3 a 300; sin dato, 30). */
    duracion_seg?: number;
    prompt?: string;
    format?: string;
    duration?: number;
    quality?: string;
    /** El usuario eligió la calidad en el menú de la card: manda sobre el default del modo. */
    calidad_elegida?: boolean;
    resolution?: string;
    autogenerate?: boolean;
    first_frame?: VideoFrameSpec;
    last_frame?: VideoFrameSpec;
    reference_images?: VideoRefSpec[];
    reference_video?: VideoReferenciaSpec;
    pending_generation?: PendingGenerationInput | null;
    /** "Pasar a 1080p" en curso sobre el borrador que salió (ADR-2026-09-27): la card vuelve
     *  a "Generando…" y se retoma al montar por `execution_id`. */
    pending_1080p?: { execution_id: string; started_at: number } | null;
    /** El 1080p ya salió: la card lo muestra en lugar del borrador. */
    final_1080p?: { video_id: string } | null;
    /** true = card empujada por el MODO DIRECTO (sin agente): el costo ya se
     *  confirmó en la pill del composer antes de tocar Generar, así que el
     *  video puede autogenerar (la regla ADR-146/147 de "el usuario siempre
     *  confirma viendo el costo" se cumple en el composer, no acá). */
    direct?: boolean;
  };
  const isVideo = mediaType === 'video';
  // Si autogenera o espera el click ya viene decidido en el input: el motor lo pisa según
  // la preferencia del usuario antes de crear la card, y el modo directo lo trae en true
  // porque el costo se confirmó en el composer
  // (ADR-2026-09-08-permisos-de-generacion-del-chat). Acá NO se vuelve a decidir: tenerlo
  // en dos lugares fue lo que hizo que el video ignorara la preferencia.
  const autogenerate = !!input.autogenerate;
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  // Pasar a 1080p el borrador que salió (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p):
  // estado local de la card; la fila nueva vive en Creaciones y el lienzo.
  const [confirmando1080p, setConfirmando1080p] = useState(false);
  const [errorPasarA1080p, setErrorPasarA1080p] = useState<string | null>(null);
  // La URL firmada del 1080p (`input.final_1080p` guarda sólo el id: las firmas vencen).
  // Se firma por 24 h, como el video de la card (`fetchGeneratedVideoUrl`): con la de
  // 1 h, una card abierta más de una hora dejaba de reproducir y descargar.
  const [urlFinal1080p, setUrlFinal1080p] = useState<string | null>(null);
  const idFinal1080p = input.final_1080p?.video_id;
  useEffect(() => {
    if (!idFinal1080p || !companyId) return;
    let vivo = true;
    void import('./agentGenerate')
      .then((g) => g.fetchGeneratedVideoUrl(idFinal1080p, companyId))
      .then((url) => {
        if (vivo) setUrlFinal1080p(url);
      })
      .catch(() => {
        if (vivo) setUrlFinal1080p(null);
      });
    return () => {
      vivo = false;
    };
  }, [idFinal1080p, companyId]);
  // Retomar un "Pasar a 1080p" que quedó en curso al recargar (mismo criterio que
  // `pending_generation`): se pollea la fila nueva por su execution_id.
  const resume1080pRef = useRef(false);
  useEffect(() => {
    const pendiente = input.pending_1080p;
    if (resume1080pRef.current || !pendiente?.execution_id || !companyId) return;
    const elapsed = Date.now() - (pendiente.started_at || 0);
    if (elapsed > PENDING_VIDEO_MAX_AGE_MS) {
      patchInput({ pending_1080p: null });
      return;
    }
    resume1080pRef.current = true;
    (async () => {
      try {
        const poll = await import('./pollGeneratedByExecutionId');
        const rows = await poll.pollGeneratedByExecutionId({
          executionId: pendiente.execution_id,
          companyId,
          expectedCount: 1,
          timeoutMs: Math.max(60_000, PENDING_VIDEO_MAX_AGE_MS - elapsed),
        });
        const okRow = rows.find((r) => !r.has_error);
        if (okRow) {
          setDatosDelVideo((d: unknown) => conFinal(d, String(okRow.id)));
          patchInput({ pending_1080p: null, final_1080p: { video_id: String(okRow.id) } });
        } else if (rows.length > 0) {
          setErrorPasarA1080p(mapErrorTypeToMessage((rows[0].error_type as GenerateAdErrorType) || 'technical'));
          patchInput({ pending_1080p: null });
        }
      } catch {
        /* sin recuperación posible → la card queda como estaba */
      }
    })();
    // Al montar y cuando llega la empresa (al recargar, `companyId` arranca en null y
    // sin esto la card quedaba en "Pasando a 1080p…"): `resume1080pRef` evita el doble.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  // Si es borrador lo dice la FILA (`datos_generacion.borrador`: is_draft confirmado, vencimiento,
  // ya finalizado), no la resolución de la card: un video viejo o uno que Enhancor no marcó no
  // muestran el botón. `undefined` = todavía no se leyó.
  const [datosDelVideo, setDatosDelVideo] = useState<unknown>(undefined);
  const videoIdResuelto =
    card.status === 'resolved' && mediaType === 'video' ? (card.response?.video_id as string | undefined) : undefined;
  useEffect(() => {
    if (!videoIdResuelto || !companyId) return;
    let vivo = true;
    void supabase
      .from('imagenes_generadas')
      .select('datos_generacion')
      .eq('id', videoIdResuelto)
      .eq('id_company', companyId)
      .maybeSingle()
      .then(({ data }) => {
        if (vivo) setDatosDelVideo((data as { datos_generacion?: unknown } | null)?.datos_generacion ?? null);
      });
    return () => {
      vivo = false;
    };
  }, [videoIdResuelto, companyId]);
  const autoFiredRef = useRef(false);
  // Sheet de edición abierto (tocar una fila del resumen para cambiarla).
  const [editing, setEditing] = useState<
    null | 'format' | 'duration' | 'frame-first' | 'frame-last'
  >(null);

  // Valores vivos del resumen — leen del input de la card, que se patchea
  // con cada edición (así handleGenerate siempre usa lo editado).
  // Lo que no vino en la tool call cae a las preferencias del usuario (menú "+" →
  // Preferencias) y recién después al default de siempre
  // (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas).
  const preferencias = chat.preferenciasDeGeneracion();
  const format = input.format || preferencias.formatoVideo || 'story';
  // Calidad del video: 'profesional' (Seedance 2.5, hasta 30s — DEFAULT desde
  // 2026-08-30) o 'basica' (Seedance 2, hasta 15s, más barata). El agente
  // puede preseleccionarla en la tool call; el usuario la cambia en el sheet.
  // El video adjunto a copiar editándolo: se resuelve por nombre entre los adjuntos del
  // chat (como hace `ver_video`). Con él, la calidad la pone el MODO, igual que en el
  // backend (`calidadConVideoDeReferencia`): básica para editar y copiar (Seedance 2
  // reproduce el video), profesional para cambiar la cámara (sólo 2.5 corta entre ángulos),
  // y la duración no pasa del tramo que queda del original
  // (ADR-2026-09-24-copiar-un-video-es-editarlo,
  // ADR-2026-09-25-cambiar-la-camara-de-un-video-va-a-calidad-profesional).
  const videoRefSpec = isVideo ? input.reference_video : undefined;
  const eleccionDelVideoRef = videoRefSpec
    ? elegirVideoAdjuntado(chat.messages, videoRefSpec.archivo)
    : null;
  const videoAdjunto = eleccionDelVideoRef?.elegido ?? null;
  // Con un video de referencia, la duración sale del VIDEO: editarlo devuelve el mismo
  // video, que dura lo mismo. Si el adjunto no trae su duración (muchos de Creaciones no
  // la guardan) se mide leyendo sus metadatos
  // (ADR-2026-09-25-editar-un-video-conserva-su-duracion). Va ANTES de la calidad porque
  // la regla de "referencia" la mira (revisión de Greptile al PR #149).
  const [duracionMedida, setDuracionMedida] = useState<number | null>(null);
  // Si la medición falla (no se pudo firmar, el navegador no leyó los metadatos o se pasó
  // del tiempo) queda anotado, y `intentoDeMedicion` la vuelve a disparar: sin esto ninguna
  // dependencia del efecto cambiaba, la card quedaba "leyendo cuánto dura" para siempre y
  // no había forma de generar desde ella.
  const [medicionFallida, setMedicionFallida] = useState(false);
  const [intentoDeMedicion, setIntentoDeMedicion] = useState(0);
  const duracionDelVideoRef = videoAdjunto?.duracionSeg || duracionMedida || null;
  const calidadPedida: 'basica' | 'profesional' =
    input.quality === 'basica'
      ? 'basica'
      : input.quality === 'profesional'
        ? 'profesional'
        : (preferencias.calidadVideo ?? 'profesional');
  // Con video de referencia el modo pone el DEFAULT (básica en editar y copiar,
  // profesional en "camara": la básica no corta entre ángulos), pero lo que el usuario
  // elige en el menú de la card manda en cualquier modo (`calidad_elegida`), sin bloquear
  // nada: hasta acá el menú se abría y la elección se pisaba con la del modo, y el backend
  // hacía lo mismo (ADR-2026-09-25-la-calidad-elegida-manda-con-video-de-referencia,
  // ADR-2026-09-25-camara-profesional-por-defecto-sin-bloquear).
  const calidadElegida = input.calidad_elegida === true;
  const esModoCamara = !!videoAdjunto && videoRefSpec?.modo === 'camara';
  // "referencia" (un video nuevo con el estilo del adjunto) sigue la regla del Estudio:
  // básica, salvo que el tramo del video pase de 14 s, que sólo entra entero en
  // profesional (ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente).
  const tramoDelVideoRef = Math.max(0, (duracionDelVideoRef || 0) - (videoRefSpec?.desde ?? 0));
  const referenciaPideProfesional =
    !!videoAdjunto && videoRefSpec?.modo === 'referencia' && pideProfesional(tramoDelVideoRef);
  const quality: 'basica' | 'profesional' =
    videoAdjunto && !calidadElegida
      ? esModoCamara || referenciaPideProfesional
        ? 'profesional'
        : 'basica'
      : calidadPedida;
  const creditModel = quality === 'profesional' ? 'pro' : 'standard';
  const topeCalidad = quality === 'profesional' ? VIDEO_PROFESIONAL_MAX_S : VIDEO_BASICA_MAX_S;
  useEffect(() => {
    if (!videoAdjunto || videoAdjunto.duracionSeg > 0 || duracionMedida !== null) return;
    let vivo = true;
    void (async () => {
      const url = await signReferenceImage(videoAdjunto.storagePath, videoAdjunto.bucket).catch(
        () => null,
      );
      const d = url ? await duracionDeVideoPorUrl(url) : null;
      if (!vivo) return;
      if (d) {
        setDuracionMedida(d);
      } else {
        setMedicionFallida(true);
        setGenError('No pude leer cuánto dura el video. Tocá Generar para reintentar.');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [
    videoAdjunto?.storagePath,
    videoAdjunto?.bucket,
    videoAdjunto?.duracionSeg,
    duracionMedida,
    intentoDeMedicion,
  ]);
  const duracionConReferencia = videoAdjunto
    ? duracionConVideoDeReferencia({
        pedida: typeof input.duration === 'number' ? input.duration : null,
        duracionVideo: duracionDelVideoRef,
        desde: videoRefSpec?.desde ?? 0,
        topeCalidad,
      })
    : null;
  const maxDuration = videoAdjunto
    ? duracionConVideoDeReferencia({ pedida: 999, duracionVideo: duracionDelVideoRef, desde: videoRefSpec?.desde ?? 0, topeCalidad }) ?? topeCalidad
    : topeCalidad;
  // Mientras se mide, la fila muestra el tope; el botón espera la medición (ver
  // `generarSinCandado`).
  // Sin video, los segundos de la tool call y si no vinieron, la preferencia del usuario
  // (la pastilla "Duración" de la caja) antes del default
  // (ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas). Con video manda el
  // video: la preferencia no pisa lo que dura lo que se edita.
  const duration = videoAdjunto
    ? (duracionConReferencia ?? maxDuration)
    : Math.min(
        maxDuration,
        typeof input.duration === 'number'
          ? input.duration
          : (preferencias.duracionVideo ?? DURACION_POR_DEFECTO),
      );
  const midiendoDuracion = !!videoAdjunto && duracionConReferencia === null;
  // Resolución (480p/720p/1080p, default 720p — lo que siempre mandó el asistente).
  // Cambia la tarifa por segundo, no el proveedor. Cae a la preferencia como el formato,
  // la calidad y la duración (ADR-2026-09-24-la-resolucion-elegida-tambien-cae-a-la-preferencia):
  // si no vino en la tool call, vale la que el usuario eligió en el engranaje.
  const resolution = safeResolution(input.resolution || preferencias.resolucionVideo || undefined);
  const firstFrame = input.first_frame;
  const lastFrame = input.last_frame;
  // Referencias elegidas por el agente (producto/modelo/logo/adjunto). Los
  // adjuntos del usuario entran igual de forma automática.
  const refSpecs: VideoRefSpec[] = Array.isArray(input.reference_images)
    ? input.reference_images
    : [];
  const patchInput = (patch: Record<string, any>) =>
    chat.patchToolCard(card.id, { input: { ...card.input, ...patch } });
  // Las referencias como fichas con miniatura, también las generadas y las adjuntas de
  // mensajes anteriores, que antes salían sin foto
  // (ADR-2026-09-25-las-referencias-se-ven-en-la-card).
  const fichasDeReferencias = useMiniaturasDeReferencias({
    specs: (isVideo ? refSpecs : []) as SpecDeReferencia[],
    adjuntasActuales: isVideo ? chat.getReferenceImages() : [],
    mensajes: chat.messages,
    companyId,
    userId,
    productos: userProducts,
    modelos: userModels,
    palabraModelo: palabraDeModeloActual().titulo,
  });
  // Los cuadros (primer/último frame) que son una imagen generada o una adjunta anterior:
  // su miniatura se resuelve igual que una referencia. Cada posición conserva su lugar
  // (null = ese cuadro no tiene miniatura que resolver).
  const framesComoSpecs: (SpecDeReferencia | null)[] = [firstFrame, lastFrame].map((f) =>
    f && (f.source === 'generada' || (f.source === 'adjunto' && f.adjunto_id))
      ? { source: f.source, image_id: f.image_id, adjunto_id: f.adjunto_id }
      : null,
  );
  const fichasDeFrames = useMiniaturasDeReferencias({
    specs: framesComoSpecs.filter((f): f is SpecDeReferencia => !!f),
    adjuntasActuales: [],
    mensajes: chat.messages,
    companyId,
    userId,
    productos: userProducts,
    modelos: userModels,
    palabraModelo: palabraDeModeloActual().titulo,
  });
  const miniaturaDeFrame = (key: 'first' | 'last'): string | undefined => {
    const pos = key === 'first' ? 0 : 1;
    if (!framesComoSpecs[pos]) return undefined;
    const idx = framesComoSpecs.slice(0, pos).filter(Boolean).length;
    return fichasDeFrames[idx]?.thumb;
  };

  // Prompts locales de los frames source "generar" (se persisten en el input
  // al salir del campo, para tipear fluido).
  const [framePrompts, setFramePrompts] = useState<{ first: string; last: string }>({
    first: firstFrame?.source === 'generar' ? firstFrame.prompt || '' : '',
    last: lastFrame?.source === 'generar' ? lastFrame.prompt || '' : '',
  });

  // Reanudar un VIDEO interrumpido (recarga / pestaña cerrada): si quedó un
  // pendiente en el input, retomamos el poll por execution_id y resolvemos
  // la card directo con patchToolCard (mismo esquema que
  // useResumeImageGeneration — ver ese comentario).
  const resumeFiredRef = useRef(false);
  useEffect(() => {
    if (!isVideo) return;
    const pending = input.pending_generation;
    if (
      resumeFiredRef.current ||
      card.status === 'resolved' ||
      !pending?.execution_id ||
      !pending?.company_id
    )
      return;
    const elapsed = Date.now() - (pending.started_at || 0);
    if (elapsed > PENDING_VIDEO_MAX_AGE_MS) return;
    resumeFiredRef.current = true;
    setGenerating(true);
    (async () => {
      try {
        const [gen, poll] = await Promise.all([
          import('./agentGenerate'),
          import('./pollGeneratedByExecutionId'),
        ]);
        const rows = await poll.pollGeneratedByExecutionId({
          executionId: pending.execution_id!,
          companyId: pending.company_id!,
          expectedCount: 1,
          // Pollear solo lo que queda de la ventana original del video.
          timeoutMs: Math.max(60_000, PENDING_VIDEO_MAX_AGE_MS - elapsed),
        });
        const okRow = rows.find((r) => !r.has_error);
        if (okRow) {
          const videoUrl = await gen.fetchGeneratedVideoUrl(
            okRow.id,
            pending.company_id,
          );
          chat.patchToolCard(card.id, {
            status: 'resolved',
            response: {
              confirmed: true,
              video_url: videoUrl,
              video_id: String(okRow.id),
            },
          });
        } else if (rows.length > 0) {
          // El video FALLÓ mientras la pestaña no estaba: la fila has_error del backend
          // trae el tipo, y la card lo dice en vez de volver al botón como si nada
          // (ADR-2026-09-24-copiar-un-video-es-editarlo, incidente de la card muda).
          const t = (rows[0].error_type as GenerateAdErrorType) || 'technical';
          setGenError(mapErrorTypeToMessage(t));
          patchInput({ pending_generation: null });
        }
      } catch {
        /* sin recuperación posible → la card queda como estaba */
      }
      setGenerating(false);
    })();
    // Solo al montar: el pendiente viene del input persistido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Costo en créditos — misma fuente de precios que el Estudio de Video
  // (utils/credits.ts). La tarifa por segundo sale de calidad × resolución
  // (básica → standard, profesional → pro / Seedance 2.5). Cada frame source
  // "generar" suma una imagen aparte (se genera y cobra por separado).
  const genFramesCount =
    (firstFrame?.source === 'generar' ? 1 : 0) +
    (lastFrame?.source === 'generar' ? 1 : 0);
  const creditCost = !isVideo
    ? input.audio_type === 'musica'
      ? creditsForSong(input.duracion_seg)
      : input.audio_type === 'efecto'
        ? creditsForEfecto()
        : creditsForVoice((input.text || '').length)
    : creditsForVideoGeneral({
        durationSec: duration,
        model: creditModel,
        resolution,
      }) +
      genFramesCount * creditsForImage();

  // El video sin ids adentro (ADR-2026-09-25-los-ids-no-salen-en-el-prompt-ni-al-usuario).
  const promptText =
    mediaType === 'audio' ? input.text || '' : limpiarIdsDelPrompt(input.prompt || '');
  // El label era "Video" fijo — y este mismo componente renderiza los audios,
  // así que un audio decía "Video". Ahora dice qué es cada cosa.
  const promptLabel = isVideo
    ? 'Prompt de video'
    : input.audio_type === 'musica'
      ? 'Estilo de la música'
      : input.audio_type === 'efecto'
        ? 'Efecto de sonido'
        : 'Guion';

  const generarSinCandado = async () => {
    if (!active || generating) return;
    setGenError(null);
    if (!userId) {
      setGenError('No pudimos identificar al usuario. Probá recargar.');
      return;
    }
    // Un video pedido para editar que no está entre los adjuntos del chat corta ACÁ, antes
    // de gastar: si no, la card caía a un video nuevo desde el texto —sin la fila "Video" y
    // hasta en calidad profesional— y el usuario pagaba otra cosa que la edición que pidió.
    // Mismo trato que una foto adjunta que falta
    // (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir): la card queda
    // resuelta con el motivo y el agente se lo pide al usuario.
    if (videoRefSpec && !videoAdjunto) {
      const nombres = eleccionDelVideoRef?.nombres ?? [];
      const motivo =
        nombres.length > 0
          ? `No hay ningún video adjunto que se llame "${videoRefSpec.archivo ?? ''}"; los videos del chat son: ${nombres.join(', ')}. No se generó nada: volvé a llamar con uno de esos nombres, o pedile al usuario que adjunte el video.`
          : 'Pediste editar un video, pero no hay ningún video adjunto en el chat. No se generó nada: pedile al usuario que lo adjunte (con el "+" o desde Creaciones) y después volvé a generar.';
      const aviso = 'No encuentro el video que querés editar. Adjuntalo y te lo armo.';
      setGenError(aviso);
      chat.resolveToolCard(card.id, { confirmed: false, error: motivo, aviso });
      return;
    }
    // Una foto "adjunto" (referencia o cuadro) que no existe corta igual que en imagen: el
    // video salía sin el producto o sin el encuadre pedido, y cobrado
    // (ADR-2026-09-24-adjunto-id-en-video). Los cuadros sin `adjunto_id` usan cualquier
    // imagen enganchada (`getReferenceImages`), por eso se cuentan todas y no sólo las
    // de tipo "adjunto".
    if (isVideo) {
      const falta =
        adjuntaQueFalta(
          refSpecs,
          chat.getReferenciasEtiquetadas().filter((r) => r.tipo === 'adjunto').length,
          chat.messages,
        ) ??
        adjuntaQueFalta(
          // Con video de referencia los cuadros no viajan (ver `cuadrosQueViajan`).
          (videoAdjunto ? [] : [firstFrame, lastFrame]).filter(
            (f): f is VideoFrameSpec => !!f,
          ),
          chat.getReferenceImages().length,
          chat.messages,
        );
      if (falta) {
        const aviso = 'Falta la foto que pediste usar. Adjuntala y te armo el video.';
        setGenError(aviso);
        chat.resolveToolCard(card.id, { confirmed: false, error: falta, aviso });
        // En modo directo la card corre tapada por la pantalla de carga: se baja, como
        // con cualquier fallo (ver el `catch` de abajo).
        if (input.direct === true) {
          try {
            window.dispatchEvent(new Event('atlas:direct-media-error'));
          } catch {
            /* noop */
          }
        }
        return;
      }
    }
    if (midiendoDuracion) {
      // Todavía no se sabe cuánto dura el video de referencia: sin eso se generaría con
      // una duración inventada y el backend recortaría el video
      // (ADR-2026-09-25-editar-un-video-conserva-su-duracion). Si la medición falló, el
      // click la vuelve a disparar.
      if (medicionFallida) {
        setMedicionFallida(false);
        setIntentoDeMedicion((n) => n + 1);
      }
      setGenError('Estoy leyendo cuánto dura el video. Probá de nuevo en un segundo.');
      return;
    }
    if (!promptText.trim()) {
      setGenError(
        mediaType === 'audio'
          ? 'Escribí el texto del audio.'
          : 'Escribí qué video querés generar.',
      );
      return;
    }
    setGenerating(true);
    try {
      if (companyId) {
        const { data: cp } = await supabase
          .from('company_profiles')
          .select('imagenes_restantes:imagenes_disponibles')
          .eq('id_company', companyId)
          .single();
        const left = Number((cp as any)?.imagenes_restantes ?? 0);
        if (left < creditCost) {
          setGenError(
            'No te alcanzan los créditos para esta generación. Sumá créditos para continuar.',
          );
          setGenerating(false);
          return;
        }
      }
      const gen = await import('./agentGenerate');
      let response: any;
      if (mediaType === 'audio') {
        const { audioUrl } = await gen.generateVoiceAudio({
          userId,
          text: input.text || '',
          audioType:
            input.audio_type === 'musica' || input.audio_type === 'efecto'
              ? input.audio_type
              : 'voz',
          durationSec: input.duracion_seg,
        });
        response = { confirmed: true, audio_url: audioUrl };
      } else {
        // Imágenes adjuntas por el usuario en el último mensaje.
        const attachedImages = chat.getReferenceImages();
        const formatSlug = format;

        // El video a editar, firmado desde su carpeta del bucket (el mismo camino que la
        // imagen adjunta por id). Va PRIMERO, antes de generar cuadros que se cobran: sin
        // URL no se genera, porque el backend haría un video nuevo desde el texto y se
        // cobraría como si fuera la edición. La card queda para reintentar.
        const videoRefUrl = videoAdjunto
          ? await signReferenceImage(videoAdjunto.storagePath, videoAdjunto.bucket).catch(
              () => null,
            )
          : null;
        if (videoAdjunto && !videoRefUrl) {
          throw new Error('No pude abrir el video que querés editar. Probá de nuevo.');
        }

        // Referencias = adjuntos automáticos + las que eligió el agente
        // (producto/modelo/logo por su foto real). Guían el look del video
        // sin fijar frames, y también la generación de frames "generar".
        // Etiquetadas desde el vamos: el tipo de cada una es lo que el backend convierte
        // en la leyenda `@Image1 es el producto…` del prompt del video
        // (ADR-2026-09-12-las-referencias-del-video-se-nombran-en-el-prompt). Antes se
        // juntaban como URLs sueltas y el tipo —que acá se conoce, spec por spec— se
        // perdía a un paso de usarse.
        //
        // Las adjuntas entran con SU tipo (un producto, una pose o un fondo enganchados
        // desde el "+" dejan de viajar como "adjunto") y con la etiqueta que el agente
        // les puso, igual que en imagen: `etiquetarAdjuntas` con las specs "adjunto"
        // (ADR-2026-09-24-las-referencias-del-video-llevan-la-etiqueta-del-agente).
        const refs: ReferenciaEtiquetada[] = etiquetarAdjuntas(
          chat.getReferenciasEtiquetadas(),
          refSpecs.filter((s) => s.source === 'adjunto' && !s.adjunto_id),
        );
        const conEtiqueta = (r: ReferenciaEtiquetada, spec: VideoRefSpec): ReferenciaEtiquetada =>
          spec.etiqueta?.trim() ? { ...r, etiqueta: spec.etiqueta.trim() } : r;
        // Una imagen subida en un mensaje ANTERIOR: ya no viaja en el historial, pero sigue
        // en Storage y el marcador le dio un id. El mismo camino que la imagen
        // (ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id); en video faltaba,
        // y "subió 4 fotos, después dijo «dale»" salía sin ninguna referencia
        // (ADR-2026-09-24-adjunto-id-en-video).
        // Que exista ya lo miró `adjuntaQueFalta` antes de cobrar; si no se deja firmar, se
        // corta acá y la card queda para reintentar: seguir sin ella era pagar un video sin
        // el producto que se pidió.
        for (const spec of refSpecs) {
          if (spec.source !== 'adjunto' || !spec.adjunto_id) continue;
          const a = adjuntoPorId(chat.messages, spec.adjunto_id);
          const u = a
            ? await signReferenceImage(a.storagePath, a.bucket).catch(() => null)
            : null;
          if (!u) throw new Error('No pude abrir una de las fotos adjuntas. Probá de nuevo.');
          refs.push(conEtiqueta({ url: u, tipo: 'adjunto' }, spec));
        }
        // Las vistas EXTRA de cada producto (la segunda foto en adelante). Van sólo al
        // video, que las elige por importancia contra el resto; el frame es una imagen y
        // su backend ya expande el producto por su id
        // (ADR-2026-09-24-el-video-elige-sus-referencias-por-importancia).
        const vistasExtra: ReferenciaEtiquetada[] = [];
        // TODAS las fotos de los productos pedidos, en un solo viaje (principal primero,
        // hasta 4 por producto, con el nombre de su vista). Si falla, cada producto cae
        // al camino de siempre: su foto principal.
        const idsDeProductos = refSpecs.flatMap((s) =>
          s.source === 'producto' && s.product_id ? [s.product_id] : [],
        );
        const fotosPorProducto: Map<string, FotoDeProducto[]> =
          companyId && idsDeProductos.length > 0
            ? await fetchProductVistas(companyId, idsDeProductos).catch(() => new Map())
            : new Map();
        let modeloSinFoto = false;
        for (const spec of refSpecs) {
          if (spec.source === 'producto') {
            const fotos = spec.product_id ? (fotosPorProducto.get(spec.product_id) ?? []) : [];
            if (fotos.length > 0 && spec.product_id) {
              const productId = spec.product_id;
              const [principal, ...resto] = fotos.map((f) => ({
                url: f.url,
                tipo: 'producto' as const,
                productId,
                ...(f.vista ? { vista: f.vista } : {}),
              }));
              refs.push(principal);
              vistasExtra.push(...resto);
              continue;
            }
            // Foto REAL del producto en tamaño FULL — el imageUrl del
            // catálogo es el thumb `opt` de 300px, pobre como referencia de
            // fidelidad (ADR-586). Fallback al thumb si el full falla.
            let u: string | null | undefined = null;
            if (spec.product_id && companyId) {
              u = await getProductFullImageUrl(companyId, spec.product_id).catch(
                () => null,
              );
            }
            if (!u) {
              u = userProducts.find((p) => p.id === spec.product_id)?.imageUrl;
            }
            if (u) refs.push({ url: u, tipo: 'producto', ...(spec.product_id ? { productId: spec.product_id } : {}) });
          } else if (spec.source === 'modelo') {
            // Re-firmada acá y no la `thumbnailUrl` de la lista: ésa se firmó al cargar
            // y dura una hora; en un chat largo llegaba vencida al worker
            // (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
            const m = userModels.find((mm) => String(mm.id) === String(spec.model_id));
            const u = m ? await fotoDeModeloParaReferencia(m) : null;
            // Sin la foto del modelo elegido no se genera: saldría otra persona y se
            // cobraría igual (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
            if (m && !u) modeloSinFoto = true;
            if (u) refs.push({ url: u, tipo: 'modelo' });
          } else if (spec.source === 'logo' && companyId) {
            const b = await fetchBrandContext(userId, companyId);
            if (b?.logoUrl) refs.push(referenciaDeLogo(b.logoUrl));
          } else if (spec.source === 'generada' && spec.image_id && companyId) {
            // Imagen ya generada en el chat → URL firmada por su id (UUID —
            // NO hacer Number(), rompería el match).
            const imgs = await preloadGeneratedImages(
              [spec.image_id],
              companyId,
            );
            if (imgs[0]?.signedUrl)
              refs.push(conEtiqueta({ url: imgs[0].signedUrl, tipo: 'generada' }, spec));
          }
          // 'adjunto' ya está cubierto por `getReferenciasEtiquetadas`, con su etiqueta.
        }
        // Sin repetidas y recortadas al tope del VIDEO (6, el que valida la ruta):
        // pasarse era un 400 que se llevaba la tanda entera
        // (ADR-2026-09-12-el-video-recortaba-en-la-ruta-y-no-en-la-pantalla). Al video
        // entran también las vistas extra de cada producto, elegidas por importancia
        // contra el resto (ADR-2026-09-24-el-video-elige-sus-referencias-por-importancia).
        // El FRAME es una imagen: va con una referencia por producto, con su id, y el
        // backend de imagen le baja sus vistas. Por la misma ruta que las imágenes del
        // chat: un link de afuera (Tiendanube) viaja sólo con el id, porque esa ruta
        // rebota lo que no es de nuestro Storage
        // (ADR-2026-09-21-el-producto-recien-traido-viaja-por-id-y-no-por-link).
        if (modeloSinFoto) throw new ReferenciaIrrecuperable(MENSAJE_MODELO_SIN_FOTO);
        // Las dos listas con las firmas por vencer renovadas
        // (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
        const referenceImages = await conReferenciasFrescas(
          elegirReferenciasDeVideo([...refs, ...vistasExtra], MAX_REFERENCIAS_VIDEO),
        );
        const referenciasDelFrame: ReferenciaEtiquetada[] = await conReferenciasFrescas(
          priorizarReferencias(
            refs.map((r) =>
              r.tipo === 'producto' && r.productId && r.url ? referenciaDeProducto(r.url, r.productId) : r,
            ),
            MAX_REFERENCIAS_VIDEO,
          ),
        );

        // Resuelve un frame (primer/último) a una URL firmada. Fuentes aptas:
        // "generar" (creamos una imagen a propósito, con las referencias como
        // guía), "adjunto" (foto del usuario) o "generada" (imagen ya
        // generada en el chat, por su id). Las fotos crudas de producto/
        // modelo/logo NO son aptas como frame — van como referencia; specs
        // viejas con esas fuentes se ignoran. Devuelve también el image_id
        // de la imagen del frame (generada o reusada) — la card lo expone en
        // su respuesta para que el agente pueda REUSAR el frame en una
        // re-edición (source "generada") en vez de regenerarlo.
        const resolveFrame = async (
          spec?: VideoFrameSpec,
        ): Promise<{ url: string | null; imageId?: string }> => {
          if (!spec || !spec.source) return { url: null };
          if (spec.source === 'adjunto') {
            // Con `adjunto_id`, la imagen de un mensaje anterior (ADR-2026-09-24-adjunto-id-en-video).
            if (spec.adjunto_id) {
              const a = adjuntoPorId(chat.messages, spec.adjunto_id);
              const u = a ? await signReferenceImage(a.storagePath, a.bucket).catch(() => null) : null;
              return { url: u || null };
            }
            return { url: attachedImages[0] || null };
          }
          if (spec.source === 'generada') {
            if (!spec.image_id || !companyId) return { url: null };
            // image_id es UUID → pasarlo tal cual (Number() daría NaN y el
            // frame nunca se adjuntaría).
            const imgs = await preloadGeneratedImages(
              [spec.image_id],
              companyId,
            );
            return {
              url: imgs[0]?.signedUrl || null,
              imageId: String(spec.image_id),
            };
          }
          if (spec.source === 'generar' && spec.prompt?.trim()) {
            const { imageIds, companyId: cid } = await gen.generateFreeformImage({
              userId,
              prompt: spec.prompt,
              formatSlug,
              companyId,
              ...(referenciasDelFrame.length > 0
                ? { referenceImages: referenciasDelFrame }
                : {}),
            });
            const firstId = imageIds[0];
            if (firstId === undefined || firstId === null) return { url: null };
            // firstId es UUID (del webhook) → tal cual, sin Number().
            const imgs = await preloadGeneratedImages(
              [firstId],
              cid || companyId || '',
            );
            return {
              url: imgs[0]?.signedUrl || null,
              imageId: String(firstId),
            };
          }
          return { url: null };
        };

        // Con video de referencia NO hay cuadros: el backend rechaza la combinación (el
        // proveedor del video de referencia no los usa y se perdían en silencio —
        // ADR-2026-09-25-revision-greptile-video-de-referencia-no-se-combina). Se ignoran
        // ANTES de resolverlos: un cuadro `generar` se cobra como imagen y se tiraba.
        const cuadrosQueViajan = videoAdjunto
          ? { first: undefined, last: undefined }
          : { first: input.first_frame, last: input.last_frame };
        const [firstFrameRes, lastFrameRes] = await Promise.all([
          resolveFrame(cuadrosQueViajan.first),
          resolveFrame(cuadrosQueViajan.last),
        ]);
        // Un cuadro pedido que no se pudo resolver corta en vez de salir sin él: el video
        // perdía el arranque (o el cierre) que el agente armó y se cobraba igual. Sólo los
        // bien formados; las fuentes que no son aptas como cuadro se ignoran, como siempre.
        const cuadroPedido = (spec?: VideoFrameSpec): boolean =>
          !!spec &&
          (spec.source === 'adjunto' ||
            (spec.source === 'generada' && !!spec.image_id) ||
            (spec.source === 'generar' && !!spec.prompt?.trim()));
        if (
          (cuadroPedido(cuadrosQueViajan.first) && !firstFrameRes.url) ||
          (cuadroPedido(cuadrosQueViajan.last) && !lastFrameRes.url)
        ) {
          throw new Error('No pude preparar el primer o el último cuadro del video. Probá de nuevo.');
        }
        const firstFrameUrl = firstFrameRes.url;
        const lastFrameUrl = lastFrameRes.url;

        // Correlación para RECUPERAR el video si el fetch muere (recarga /
        // pestaña cerrada). Se marca DESPUÉS de resolver los frames — recién
        // acá arranca la generación del video en sí.
        const executionId = crypto.randomUUID();
        if (companyId) {
          chat.patchToolCard(card.id, {
            input: {
              ...card.input,
              pending_generation: {
                execution_id: executionId,
                company_id: companyId,
                expected_count: 1,
                started_at: Date.now(),
              },
            },
          });
        }

        // El video a editar ya se firmó arriba, antes de los cuadros.
        const referenceVideos = videoRefUrl
          ? [
              {
                url: videoRefUrl,
                ...(videoRefSpec?.desde ? { desde: videoRefSpec.desde } : {}),
                ...(videoRefSpec?.modo === 'copiar' ||
                videoRefSpec?.modo === 'camara' ||
                videoRefSpec?.modo === 'referencia'
                  ? { modo: videoRefSpec.modo }
                  : {}),
              },
            ]
          : [];

        const { videoUrl, videoId } = await gen.generateVideoFromPrompt({
          userId,
          prompt: promptText,
          formatSlug,
          durationSeconds: duration,
          quality,
          ...(videoAdjunto && calidadElegida ? { calidadElegida: true } : {}),
          resolution,
          companyId,
          // El TIPO de video que el usuario dejó puesto en la pastilla de la caja: con él,
          // el backend escribe el prompt guiado por la skill de ese tipo, igual que en el
          // Creador de video (ADR-2026-09-22-el-tipo-de-video-tambien-en-la-caja-del-agente).
          // Sin tipo (General) no viaja nada y el prompt es el del agente, como siempre.
          ...(preferencias.tipoVideo ? { tipoVideo: preferencias.tipoVideo } : {}),
          ...(referenceImages.length > 0 ? { referenceImages } : {}),
          ...(referenceVideos.length > 0 ? { referenceVideos } : {}),
          ...(firstFrameUrl ? { firstFrameUrl } : {}),
          ...(lastFrameUrl ? { lastFrameUrl } : {}),
          ...(companyId ? { executionId } : {}),
        });
        response = {
          confirmed: true,
          video_url: videoUrl,
          // Fila de imagenes_generadas (cuando el backend la escribió) — el
          // modo directo navega a la pantalla de generadas con este id.
          ...(videoId ? { video_id: videoId } : {}),
          // Ids de las imágenes de los frames (generadas o reusadas) — el
          // agente los necesita para re-editar el video reusando el frame.
          ...(firstFrameRes.imageId
            ? { first_frame_image_id: firstFrameRes.imageId }
            : {}),
          ...(lastFrameRes.imageId
            ? { last_frame_image_id: lastFrameRes.imageId }
            : {}),
        };
      }
      // Sumamos el costo al tally de créditos del chat (igual que las
      // generaciones de imagen).
      chat.addChatCredits(creditCost);
      // El webhook descontó los créditos en el backend → refrescamos el pill.
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* noop */
      }
      sonarListo();
      chat.resolveToolCard(card.id, response);
    } catch (e: any) {
      console.error('[MediaGenerate] generación falló', e);
      // Fallo definitivo (el poll también agotó) → nada que retomar.
      if (isVideo) patchInput({ pending_generation: null });
      setGenError(e?.message || 'No se pudo generar. Probá de nuevo.');
      setGenerating(false);
      // MODO DIRECTO: la card corre TAPADA por la pantalla de carga del
      // AsistenteMode (la barra de video dura ~10 min) — sin esta señal, un
      // fallo temprano dejaría al usuario mirando la carga hasta el final.
      // El listener baja el overlay y la card queda a la vista con su error.
      if (isVideo && input.direct === true) {
        try {
          window.dispatchEvent(new Event('atlas:direct-media-error'));
        } catch {
          /* noop */
        }
      }
    }
  };
  /**
   * El cerrojo de doble click, ANTES de cualquier `await`
   * (ADR-2026-09-14-generar-una-sola-vez-por-click). `generating` es estado: dos clicks
   * en el mismo tick lo ven en false los dos, y la consulta de saldo de adentro tarda lo
   * suficiente para que el segundo pase. Es el mismo bug —y el mismo cerrojo por id de
   * card— que ya tuvo el lote (ADR-2026-09-13-el-lote-se-lanzaba-dos-veces). Cubre también
   * el botón "Generar sin preguntarme", que llama a este mismo handler.
   */
  const handleGenerate = async () => {
    if (!reservarLanzamiento(card.id)) return;
    try {
      await generarSinCandado();
    } finally {
      liberarLanzamiento(card.id);
    }
  };

  // Auto-generar — solo AUDIO (para video `autogenerate` es siempre false:
  // el usuario confirma desde el resumen viendo el costo).
  useEffect(() => {
    if (
      autogenerate &&
      active &&
      card.status !== 'resolved' &&
      !autoFiredRef.current &&
      promptText.trim()
    ) {
      autoFiredRef.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autogenerate, active, card.status]);

  if (card.status === 'resolved') {
    const audioUrl: string | undefined = card.response?.audio_url;
    const videoUrl: string | undefined = card.response?.video_url;
    if (mediaType === 'audio' && audioUrl) {
      return (
        <div className="p-4">
          <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
            Audio generado
          </p>
          <audio controls src={audioUrl} className="w-full" />
        </div>
      );
    }
    if (mediaType === 'video' && videoUrl) {
      const handleDownload = async () => {
        try {
          const res = await fetch(urlFinal1080p ?? videoUrl);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `atlas-video-${Date.now()}.mp4`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch {
          window.open(urlFinal1080p ?? videoUrl, '_blank');
        }
      };
      const videoIdBorrador = card.response?.video_id as string | undefined;
      const pendiente1080p = input.pending_1080p;
      const esBorradorVigente =
        !idFinal1080p && !pendiente1080p && !!videoIdBorrador && borradorVigente(datosDelVideo);
      const segundosDelBorrador = borradorDe(datosDelVideo)?.segundos ?? duration;
      const creditos1080p = creditosDePasarA1080p(segundosDelBorrador);
      // Al confirmar, la card vuelve a "Generando…" (como cualquier video) y el popup se
      // cierra; el pedido lleva su execution_id para poder retomarlo tras una recarga.
      const pasarA1080p = () => {
        if (!videoIdBorrador || pendiente1080p || !companyId) return;
        const executionId = crypto.randomUUID();
        setConfirmando1080p(false);
        setErrorPasarA1080p(null);
        patchInput({ pending_1080p: { execution_id: executionId, started_at: Date.now() } });
        void (async () => {
          try {
            const r = await pasarBorradorA1080p({ videoId: videoIdBorrador, executionId });
            if (!r.ok || !r.videoId) {
              setErrorPasarA1080p(r.ok ? 'El video salió pero no pudimos cargarlo. Mirá Creaciones.' : r.mensaje);
              patchInput({ pending_1080p: null });
              return;
            }
            const finalId = r.videoId;
            setDatosDelVideo((d: unknown) => conFinal(d, finalId));
            patchInput({ pending_1080p: null, final_1080p: { video_id: finalId } });
            chat.addChatCredits(creditos1080p);
            sonarListo();
          } finally {
            try {
              window.dispatchEvent(new Event('atlas:credits-updated'));
            } catch {
              /* noop */
            }
          }
        })();
      };
      if (pendiente1080p) {
        return (
          <GenerandoEnCard
            cantidad={1}
            format={input.format || 'story'}
            creadaEn={pendiente1080p.started_at}
            esVideo
            duracionMs={estimarDuracionVideoMs({
              quality: 'profesional',
              resolution: '1080p',
              durationSeconds: segundosDelBorrador,
            })}
            subtexto="Pasando a 1080p: sale la misma toma en alta. Puede tardar unos minutos."
          />
        );
      }
      return (
        <div className="p-3">
          {idFinal1080p && (
            <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
              1080p
            </p>
          )}
          {/* El container se ajusta al video (sin barras negras): el
              <video> se muestra a su aspecto natural, centrado, con una
              altura máxima para que no quede gigante. */}
          <video
            controls
            playsInline
            src={urlFinal1080p ?? videoUrl}
            className="block mx-auto max-h-[60dvh] max-w-full w-auto rounded-xl"
          />
          <button
            type="button"
            onClick={handleDownload}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-brand-black/85 text-[14px] font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Descargar video
          </button>
          {/* Borrador (Profesional 480p): misma toma en 1080p, cobra la tarifa 1080p
              (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). */}
          {errorPasarA1080p && <p className="mt-2 text-[13px] text-red-600">{errorPasarA1080p}</p>}
          {esBorradorVigente && (
            <button
              type="button"
              onClick={() => setConfirmando1080p(true)}
              title={AYUDA_BORRADOR}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-brand-black/85 text-[14px] font-medium transition-colors"
            >
              <ArrowUpFromLine className="w-4 h-4" />
              {ROTULO_PASAR_A_1080P}
            </button>
          )}
          <PasarA1080pDialogo
            abierto={confirmando1080p}
            creditos={creditos1080p}
            segundos={segundosDelBorrador}
            procesando={false}
            onCerrar={() => setConfirmando1080p(false)}
            onConfirmar={pasarA1080p}
          />
        </div>
      );
    }
    // Cortada antes de generar (falta el video o una foto adjunta): decir "Listo." sería
    // mentir; se muestra el motivo corto que dejó la card al resolverse.
    const aviso: string | undefined =
      card.response?.confirmed === false ? card.response?.aviso : undefined;
    return (
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-brand-primary" />
        </div>
        <p className="text-[14px] text-brand-black/85 leading-snug">{aviso || 'Listo.'}</p>
      </div>
    );
  }

  if (generating || (autogenerate && !genError)) {
    // El video avisa su demora ACÁ y no solo en el texto del agente: es el
    // container que el usuario mira mientras espera, y la espera real son
    // minutos — sin el aviso parece colgado.
    // Misma animación que el panel del modo Modelo: el recuadro con la proporción del
    // formato del video y la barra paceada a minutos; el audio, la versión baja con el
    // ecualizador (ADR-2026-09-09-video-y-audio-generan-como-el-panel).
    return mediaType === 'video' ? (
      <GenerandoEnCard
        cantidad={1}
        format={input.format || 'story'}
        creadaEn={input.pending_generation?.started_at}
        esVideo
        duracionMs={DURACION_BARRA_VIDEO_MS}
        subtexto="Puede tardar entre 5 y 15 minutos. Podés seguir usando la app."
      />
    ) : (
      <GenerandoAudioEnCard />
    );
  }

  // Botón Generar con el costo en créditos — mismo badge que las cards de
  // imagen (token_icon + número).
  const generateButton = (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={!active || generating}
      className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
    >
      <Sparkles className="w-4.5 h-4.5" />
      <span>Generar</span>
      <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
        <img
          src="/images/token_icon.png"
          alt=""
          aria-hidden="true"
          className="w-3.5 h-3.5 object-contain"
        />
        {creditCost}
      </span>
    </button>
  );

  const errorBox = genError && (
    <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
      {genError}
    </div>
  );

  // ── AUDIO: form simple (texto + Generar con costo) ────────────────────
  if (!isVideo) {
    return (
      <div className="p-5">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          Audio
        </p>
        <p className="text-[14px] text-brand-black/85 leading-snug mb-4 whitespace-pre-wrap">
          {promptText}
        </p>
        {errorBox}
        {generateButton}
      </div>
    );
  }

  // ── VIDEO: resumen editable (mismo patrón visual que la card de imagen) ─
  // Fila del resumen — clickeable abre el sheet correspondiente. La columna
  // del label es más ancha que en la card de imagen (w-24) porque acá hay
  // labels largos ("Referencias", "1er frame") que no entran en w-16.
  const Row: React.FC<{
    label: string;
    onEdit?: () => void;
    children: React.ReactNode;
  }> = ({ label, onEdit, children }) => (
    <button
      type="button"
      onClick={() => onEdit && active && onEdit()}
      disabled={!active || !onEdit}
      className="flex items-center gap-3 -mx-2 px-2 py-1.5 rounded-lg text-left transition-colors enabled:hover:bg-neutral-50 disabled:cursor-default"
    >
      <span className="w-24 text-[11.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 shrink-0">
        {label}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
      {onEdit && <ChevronRight className="w-4 h-4 text-[#3C3C43]/35 shrink-0" />}
    </button>
  );

  // Fila de un frame (primer/último). "generar" → muestra el prompt truncado
  // y abre un sheet aparte para editarlo (como Formato/Duración — inline no
  // entra); "adjunto"/"generada" → chip fijo. Fuentes viejas
  // (producto/modelo/logo) ya no son aptas como frame y no se muestran.
  const renderFrameRow = (key: 'first' | 'last', spec?: VideoFrameSpec) => {
    if (!spec?.source) return null;
    // Labels completos — "1er frame" se leía como "PRIMER FRAME" recortado.
    const label = key === 'first' ? 'Primer frame' : 'Último frame';
    if (spec.source === 'generar') {
      return (
        <Row
          label={label}
          onEdit={() =>
            setEditing(key === 'first' ? 'frame-first' : 'frame-last')
          }
        >
          {/* Prompt completo (sin truncar — antes no se podía leer). El
              sheet queda para editar cómodo. */}
          <span className="block text-[13px] font-medium text-brand-black/85 leading-snug">
            {framePrompts[key] || 'Describir la imagen del frame'}
          </span>
        </Row>
      );
    }
    if (spec.source !== 'adjunto' && spec.source !== 'generada') return null;
    const isAdjunto = spec.source === 'adjunto';
    // Una adjunta de ESTE mensaje es la primera enganchada; una anterior (por
    // `adjunto_id`) o una generada se resuelven a su miniatura.
    const thumb =
      isAdjunto && !spec.adjunto_id ? chat.getReferenceImages()[0] : miniaturaDeFrame(key);
    const chipLabel = isAdjunto ? 'Imagen adjunta' : 'Imagen generada';
    return (
      <Row label={label}>
        <span className="flex items-center gap-1.5 bg-[#F7F7F9] rounded-full pr-3 pl-1 py-0.5 max-w-full w-fit">
          {thumb ? (
            <CachedImg
              src={thumb}
              alt={chipLabel}
              className="w-5 h-5 rounded-full object-cover"
            />
          ) : (
            <span className="w-1.5" />
          )}
          <span className="text-[12.5px] font-medium text-brand-black/85 truncate max-w-[160px]">
            {chipLabel}
          </span>
        </span>
      </Row>
    );
  };

  return (
    <div className="p-5">
      <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
        {promptLabel}
      </p>
      <p className="text-[14px] text-brand-black/85 leading-relaxed mb-4 whitespace-pre-wrap">
        {promptText}
      </p>

      <div className="flex flex-col gap-1 mb-4">
        <Row label="Formato" onEdit={() => setEditing('format')}>
          <span className="text-[13px] font-medium text-brand-black/85">
            {getFormatLabel(format) || format}
          </span>
        </Row>
        <Row label="Duración" onEdit={() => setEditing('duration')}>
          <span className="text-[13px] font-medium text-brand-black/85">
            {duration}s
          </span>
        </Row>
        <Row label="Calidad" onEdit={() => setEditing('duration')}>
          <span className="text-[13px] font-medium text-brand-black/85">
            {quality === 'profesional' ? 'Profesional' : 'Básica'}
          </span>
        </Row>
        <Row label="Resolución" onEdit={() => setEditing('duration')}>
          <span className="text-[13px] font-medium text-brand-black/85 tabular-nums">
            {resolution}
          </span>
        </Row>
        {videoAdjunto && (
          <Row label="Video">
            <span className="block text-[13px] font-medium text-brand-black/85 truncate">
              {videoAdjunto.name}
              {videoRefSpec?.desde ? ` · desde ${videoRefSpec.desde}s` : ''}
            </span>
            <span className="block text-[12px] text-[#3C3C43]/60">
              {videoRefSpec?.modo === 'copiar'
                ? 'Se copia la estructura del video con otra persona y otro lugar.'
                : videoRefSpec?.modo === 'camara'
                  ? 'Se vuelve a filmar el video con otras tomas: misma persona, mismo lugar, mismas frases.'
                  : videoRefSpec?.modo === 'referencia'
                    ? 'Es la referencia de estilo, ritmo y cámara de un video nuevo: el contenido es el del prompt.'
                    : 'Se edita el video original: misma escena, cambia lo que pediste.'}
            </span>
          </Row>
        )}
        {renderFrameRow('first', firstFrame)}
        {renderFrameRow('last', lastFrame)}
        {fichasDeReferencias.length > 0 && (
          <Row label="Referencias">
            <ChipsDeReferencias fichas={fichasDeReferencias} />
          </Row>
        )}
      </div>

      {errorBox}
      {generateButton}

      {/* Genera y apaga la confirmación. Desde el VIDEO queda en "nunca": el que no
          quiere confirmar lo más caro, menos va a querer confirmar una imagen
          (ADR-2026-09-13-generar-sin-preguntarme-en-la-card). No va en las cards del
          MODO DIRECTO: ahí el costo se confirmó en la pill del composer y los permisos
          del agente no tienen nada que ver. */}
      {!autogenerate && !input.direct && (
        <BotonGenerarSinPreguntar
          tipo="video"
          noPreguntarMas={chat.noPreguntarMas}
          onAccion={handleGenerate}
          disabled={!active || generating}
        />
      )}

      {/* ── Sheets de edición ─────────────────────────────────────────── */}
      {/* Igual que en las cards de imagen: lo que elige acá queda como su default
          (ADR-2026-09-14-cambiar-el-formato-en-la-card-cambia-el-default). El formato de
          VIDEO tiene su propia preferencia: no pisa la de imagen. */}
      <FormatPicker
        open={editing === 'format'}
        selectedId={format}
        onChange={(id) => {
          patchInput({ format: id });
          // El picker ofrece TODOS los formatos de imagen y el video acepta cinco: un
          // formato que el video no soporta no se guarda como preferencia de video.
          if (esFormatoVideo(id)) chat.recordarPreferencia('formatoVideo', id);
        }}
        onClose={() => setEditing(null)}
      />
      <VideoConfigSheet
        open={editing === 'duration'}
        duration={duration}
        onDurationChange={(n) => patchInput({ duration: n })}
        multiScene={false}
        onMultiSceneChange={() => {}}
        showMultiScene={false}
        baseCost={genFramesCount}
        quality={quality}
        onQualityChange={(id) => {
          patchInput({ quality: id, calidad_elegida: true });
          chat.recordarPreferencia('calidadVideo', id === 'basica' ? 'basica' : 'profesional');
        }}
        qualityOptions={[
          {
            id: 'profesional',
            label: 'Profesional',
            description: esModoCamara
              ? `Recomendada para cambiar la cámara · hasta ${VIDEO_PROFESIONAL_MAX_S}s · ${videoGeneralRatePerSec('pro', resolution)} créditos/seg`
              : `Mejor modelo · hasta ${VIDEO_PROFESIONAL_MAX_S}s · ${videoGeneralRatePerSec('pro', resolution)} créditos/seg`,
            costPerSecond: videoGeneralRatePerSec('pro', resolution),
            maxDuration: VIDEO_PROFESIONAL_MAX_S,
          },
          {
            id: 'basica',
            label: 'Básica',
            description: esModoCamara
              ? `Más económica · suele no cortar entre ángulos · hasta ${VIDEO_BASICA_MAX_S}s · ${videoGeneralRatePerSec('standard', resolution)} créditos/seg`
              : `Más económica · hasta ${VIDEO_BASICA_MAX_S}s · ${videoGeneralRatePerSec('standard', resolution)} créditos/seg`,
            costPerSecond: videoGeneralRatePerSec('standard', resolution),
            maxDuration: VIDEO_BASICA_MAX_S,
          },
        ]}
        resolution={resolution}
        onResolutionChange={(id) => patchInput({ resolution: id })}
        resolutionOptions={RESOLUCIONES_VIDEO.map((r) => ({
          id: r,
          label: r,
          description: `${videoGeneralRatePerSec(creditModel, r)} cr/seg`,
          ...(esBorrador(quality, r) ? { borrador: true, title: AYUDA_BORRADOR } : {}),
        }))}
        onClose={() => setEditing(null)}
      />
      <FramePromptSheet
        open={editing === 'frame-first' && firstFrame?.source === 'generar'}
        title="Primer frame"
        value={framePrompts.first}
        onChange={(v) => setFramePrompts((s) => ({ ...s, first: v }))}
        onClose={() => {
          patchInput({
            first_frame: { ...firstFrame, prompt: framePrompts.first.trim() },
          });
          setEditing(null);
        }}
      />
      <FramePromptSheet
        open={editing === 'frame-last' && lastFrame?.source === 'generar'}
        title="Último frame"
        value={framePrompts.last}
        onChange={(v) => setFramePrompts((s) => ({ ...s, last: v }))}
        onClose={() => {
          patchInput({
            last_frame: { ...lastFrame, prompt: framePrompts.last.trim() },
          });
          setEditing(null);
        }}
      />
    </div>
  );
};
